import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from 'react';
import './DataSettingsModal.css';
import { api, ApiError } from '../lib/api.js';
import { BRANDING_CHANGED, CLINICAL_SETTINGS_CHANGED } from '../lib/settingsEvents.js';
import { useSession, canManage } from '../auth/session.js';
import { emailValid, maskPhone, phoneValid } from '../lib/contactFields.js';
import { useFetch } from '../lib/useFetch.js';
import { DateField } from '../components/DateTimeField.js';
import { Dd, MultiDd } from '../components/Dropdown.js';
import {
  LATE_ENTRY_HOURS_DEFAULT,
  PRACTICE_ENTITY_TYPES,
  PRACTICE_INCORPORATION_STATES,
  PSYCH_TESTING_CODE_CHOICES,
  SERVICE_CODE_CHOICES,
  SERVICE_NOTE_KINDS,
  defaultOpenHours,
  openHoursErrors,
  type OpenHours,
} from '@ehr/shared';
import { PSYCH_TESTING_KIND, SERVICE_CODES } from '../lib/noteV81.js';
import { cptLabel } from '../lib/noteBilling.js';
import { ImportExportModal, type ImportExportKind } from './ImportExportModal.js';
import { PracticeLogoField } from '../components/PracticeLogoField.js';
import { PracticeHoursSection, openHoursSummary } from '../components/PracticeHoursSection.js';
import { AuditView } from './AdminViews.js';
import { AutoLogoutSettings } from '../components/AutoLogoutSettings.js';
import { OptOutCenter } from '../components/OptOutCenter.js';
import { PayorsSettings } from '../components/PayorsSettings.js';
import { SlidingTiersSettings } from '../components/SlidingScale.js';
import { dsFootNote } from './dataSettingsFoot.js';
import { SyncSettings } from '../components/SyncSettings.js';
import { PaymentsSettings } from '../components/PaymentsSettings.js';
import {
  pushPermission,
  requestPushPermission,
  resetPracticePush,
  setSoundOn,
  soundOn,
  type PushPermission,
} from '../lib/desktopAlerts.js';
import {
  ClientVerificationSettings,
  verificationBody,
} from '../components/ClientVerificationSettings.js';
import {
  HOLIDAY_NTH_LABELS,
  MONTH_NAMES,
  STANDARD_HOLIDAYS,
  WEEKDAY_NAMES,
  holidayDateIn,
  holidayLabel,
  verificationErrors,
  type HolidayKind,
  type VerificationSettings,
} from '@ehr/shared';
import { askConfirm } from '../lib/dialogs.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';

/** ehr#571: the late-entry hours a practice can set (1 to 720, whole hours). */
const lateHoursOk = (v: string) => /^\d+$/.test(v.trim()) && Number(v) >= 1 && Number(v) <= 720;

/**
 * Data & Settings — the twelve-section settings surface rebuilt as a tabbed
 * overlay dialog, ported 1:1 (pixel-matched) from the platform mockup
 * (OneDrive/Documents/CredifyFast/mockup-practice-settings.html). All styling is
 * scoped under `.ds-modal` (see DataSettingsModal.css).
 *
 * Phase 1 (this file): front-end only. Tabs render from local seed state that
 * mirrors the mockup, EXCEPT where a live endpoint already exists — the Audit
 * log reads `GET /audit`, the Backup counts read `GET /contacts/counts`, and the
 * bucket-alert recipients read `GET /settings/users`. Every other section (and
 * every mutation) is a client-side stand-in; persistence + the remaining models
 * land in later phases (docs/phase-20-settings-rbac-plan.md).
 */

/* ── icons (paths lifted from the mockup) ─────────────────────────────────── */
const ico = (inner: string, sw = 2): ReactNode => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={sw}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    dangerouslySetInnerHTML={{ __html: inner }}
  />
);
const I = {
  info: '<path d="M3 21h18"/><path d="M5 21V7l7-4 7 4v14"/><path d="M10 21v-5h4v5"/>',
  loc: '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>',
  hol: '<rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>',
  trash:
    '<polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>',
  card: '<rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/><line x1="6" y1="15" x2="10" y2="15"/>',
  payors: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/>',
  phone:
    '<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>',
  numbers:
    '<rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/>',
  mail: '<rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="22,6 12,13 2,6"/>',
  storage:
    '<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>',
  branding:
    '<path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>',
  notifs:
    '<path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>',
  routing:
    '<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.6" y1="10.7" x2="15.4" y2="6.3"/><line x1="8.6" y1="13.3" x2="15.4" y2="17.7"/>',
  data: '<ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>',
  audit: '<path d="M12 8v4l3 2"/><circle cx="12" cy="12" r="9"/>',
  // ehr#416: Auto logout
  autologout:
    '<rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>',
  // reminders#4: the Opt-Out Center
  optout: '<circle cx="12" cy="12" r="9"/><line x1="5.6" y1="5.6" x2="18.4" y2="18.4"/>',
  // ehr#309: Client verification
  verify:
    '<path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><polyline points="17 11 19 13 23 9"/>',
  clinical:
    '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/>',
  search: '<circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>',
  close: '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>',
  x: '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>',
  plus: '<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>',
  export:
    '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>',
  import:
    '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>',
  eyeOn: '<path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/><circle cx="12" cy="12" r="3"/>',
  eyeOff:
    '<path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><path d="M1 1l22 22"/>',
};

/* ── seed data, matching the app's real shapes (from the mockup) ──────────── */
interface Location {
  id: string;
  name: string;
  type: string;
  email: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  primary?: boolean;
  /** ehr#638: open hours; null = never set. */
  hours?: OpenHours | null;
}
interface Holiday {
  id?: string; // server id when API-backed (needed for delete)
  /** ehr#376: date = every year on date's month/day; rule = nth weekday of a month; once = that day. */
  kind?: HolidayKind;
  date: string;
  ruleNth?: number | null;
  ruleWeekday?: number | null;
  ruleMonth?: number | null;
  ruleOffset?: number;
  name: string;
  obs: string;
}
interface PhoneRow {
  num: string;
  carrier: string;
  type: string;
  loc: string;
  status: string;
  main?: boolean;
}
interface Pending {
  when: string;
  carrier: string;
  kind: string;
  type: string;
  area: string;
  qty: number;
  loc: string;
}
interface Account {
  email: string;
  provider: string;
  /** Raw provider id for API calls ('google' | 'microsoft'); display uses `provider`. */
  providerId?: string;
  status: string;
  note?: string;
}
interface Label {
  key: string;
  def: string;
  val: string;
}
interface Template {
  name: string;
  subj: string;
  ch: string;
}
interface RoutingRule {
  cond: string;
  who: string;
}

const SEED_LOCATIONS: Location[] = [
  {
    id: 'loc_sd',
    name: 'San Diego HQ',
    type: 'Office',
    email: 'frontdesk.sd@credifyfast.com',
    line1: '600 West Broadway',
    line2: 'Suite 700',
    city: 'San Diego',
    state: 'CA',
    zip: '92101',
    phone: '(858) 227-7895',
    primary: true,
  },
  {
    id: 'loc_la',
    name: 'Los Angeles',
    type: 'Office',
    email: 'frontdesk.la@credifyfast.com',
    line1: '12130 Millennium Dr',
    line2: 'Suite 300',
    city: 'Los Angeles',
    state: 'CA',
    zip: '90094',
    phone: '(310) 555-0188',
  },
  {
    id: 'loc_phx',
    name: 'Phoenix (Remote Hub)',
    type: 'Remote',
    email: 'remote.phx@credifyfast.com',
    line1: '2390 E Camelback Rd',
    line2: '',
    city: 'Phoenix',
    state: 'AZ',
    zip: '85016',
    phone: '(602) 555-0143',
  },
  {
    id: 'loc_dal',
    name: 'Dallas',
    type: 'Office',
    email: 'frontdesk.dal@credifyfast.com',
    line1: '2100 Ross Ave',
    line2: 'Suite 1100',
    city: 'Dallas',
    state: 'TX',
    zip: '75201',
    phone: '(214) 555-0166',
  },
  {
    id: 'loc_nyc',
    name: 'New York',
    type: 'Office',
    email: 'frontdesk.nyc@credifyfast.com',
    line1: '1 World Trade Center',
    line2: 'Floor 52',
    city: 'New York',
    state: 'NY',
    zip: '10007',
    phone: '(212) 555-0177',
  },
];
// ehr#376: the standard holidays a practice starts with (seen only when the API can't be reached).
const SEED_HOLIDAYS: Holiday[] = STANDARD_HOLIDAYS.map((h) => ({
  kind: h.kind,
  date: h.monthDay ? '2027-' + h.monthDay : '',
  ruleNth: h.ruleNth ?? null,
  ruleWeekday: h.ruleWeekday ?? null,
  ruleMonth: h.ruleMonth ?? null,
  ruleOffset: h.ruleOffset ?? 0,
  name: h.name,
  obs: 'All locations',
}));
/** ehr#376: the Holidays table runs January to December (this year's dates). */
const holidaySortKey = (h: Holiday) =>
  holidayDateIn(h, new Date().getFullYear())?.slice(5) ?? h.date.slice(5) + h.date.slice(0, 4);
const sortHolidays = (hs: Holiday[]) =>
  [...hs].sort((a, b) => holidaySortKey(a).localeCompare(holidaySortKey(b)));
/** Weekday choices Monday to Sunday (values are 0 = Sunday .. 6). */
const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const SEED_NUMBERS: PhoneRow[] = [
  {
    num: '+1 (858) 227-7895',
    carrier: 'Telnyx',
    type: 'Local',
    loc: 'San Diego HQ',
    status: 'Active',
    main: true,
  },
  {
    num: '+1 (858) 227-7896',
    carrier: 'Telnyx',
    type: 'Fax',
    loc: 'San Diego HQ',
    status: 'Active',
  },
  {
    num: '+1 (310) 555-0188',
    carrier: 'Telnyx',
    type: 'Local',
    loc: 'Los Angeles',
    status: 'Active',
  },
  {
    num: '+1 (800) 555-0142',
    carrier: 'Telnyx',
    type: 'Toll-free',
    loc: 'Unassigned',
    status: 'Active',
  },
  {
    num: '+1 (212) 555-0177',
    carrier: 'Telnyx',
    type: 'Local',
    loc: 'New York',
    status: 'Porting',
  },
];
const SEED_LABELS: Label[] = [
  { key: 'Prospect', def: 'Prospect', val: 'Client' },
  { key: 'Agency', def: 'Agency', val: 'Practice' },
  { key: 'Therapist', def: 'Therapist', val: '' },
  { key: 'Deal', def: 'Deal', val: 'Engagement' },
  { key: 'Task', def: 'Task', val: '' },
];
const SEED_TEMPLATES: Template[] = [
  { name: 'New referral assigned', subj: "You've been assigned {{contact_name}}", ch: 'Email' },
  { name: 'Reassignment notice', subj: '{{contact_name}} moved to your queue', ch: 'In-app' },
];
const SEED_RULES: RoutingRule[] = [
  { cond: 'State is <b>CA</b> and category is <b>Agency</b>', who: 'Jennifer Cole' },
  { cond: 'Referral source is <b>Website form</b>', who: 'Round-robin · Intake team' },
];
interface SeedUser {
  id: string;
  name: string;
  role: string;
}
const SEED_USERS: SeedUser[] = [
  { id: 'u1', name: 'Chase Whitmore', role: 'Administrator' },
  { id: 'u2', name: 'Maya Okonkwo', role: 'Credentialing Specialist' },
  { id: 'u3', name: 'Devon Reyes', role: 'Intake Coordinator' },
  { id: 'u4', name: 'Priya Raman', role: 'Billing Specialist' },
];

/* ── live-data shapes ─────────────────────────────────────────────────────── */
interface Counts {
  client?: number;
  referral_source?: number;
  staff?: number;
}
// Practice settings — live shapes from GET /practice/locations|holidays.
interface LocationRow {
  id: string;
  name: string;
  type: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  isPrimary: boolean;
  hours?: OpenHours | null; // ehr#638
}
interface HolidayRow {
  id: string;
  kind: HolidayKind;
  date: string;
  ruleNth: number | null;
  ruleWeekday: number | null;
  ruleMonth: number | null;
  ruleOffset?: number;
  name: string;
  observed: string;
}
/**
 * ehr#302 - a location's phone or front-desk email, edited in place in the Locations table:
 * it saves when you press Enter or leave the box; Escape puts the old value back.
 */
function LocContactCell({
  value,
  kind,
  label,
  disabled,
  onSave,
  onInvalid,
}: {
  value: string;
  kind: 'phone' | 'email';
  label: string;
  disabled: boolean;
  onSave: (v: string) => void;
  /** ehr#302: true while the typed value is not a valid phone / email (kept on screen, in red). */
  onInvalid: (bad: boolean) => void;
}) {
  // ehr#409: a stored 10-digit phone shows as "(858) 227-7895", as it does while typing.
  const shown = (x: string) =>
    kind === 'phone' && x.replace(/\D/g, '').length === 10 ? maskPhone(x) : x;
  const [v, setV] = useState(shown(value));
  const [bad, setBad] = useState(false);
  useEffect(() => setV(shown(value)), [value]);
  const mark = (b: boolean) => {
    setBad(b);
    onInvalid(b);
  };
  const commit = () => {
    const next = v.trim();
    const same =
      kind === 'phone' ? next.replace(/\D/g, '') === value.replace(/\D/g, '') : next === value;
    if (same) {
      if (bad) mark(false);
      return;
    }
    if (!(kind === 'phone' ? phoneValid(next) : emailValid(next))) {
      mark(true); // keep what was typed so it can be fixed; Save changes waits for it
      return;
    }
    if (bad) mark(false);
    onSave(next);
  };
  return (
    <input
      className={`loc-cell loc-cell-${kind}${bad ? ' bad' : ''}`}
      value={v}
      title={
        bad
          ? kind === 'phone'
            ? 'Use a 10-digit phone number'
            : 'Enter a valid email, like frontdesk@yourpractice.com'
          : v || undefined
      }
      aria-invalid={bad || undefined}
      aria-label={label}
      placeholder={disabled ? '—' : kind === 'phone' ? 'Add phone' : 'Add email'}
      type={kind === 'email' ? 'email' : 'tel'}
      disabled={disabled}
      onChange={(e) => setV(kind === 'phone' ? maskPhone(e.target.value) : e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') {
          e.stopPropagation(); // keep the Settings window open
          setV(shown(value));
          if (bad) mark(false);
          setTimeout(() => (e.target as HTMLInputElement).blur(), 0);
        }
      }}
    />
  );
}

const mapLoc = (l: LocationRow): Location => ({
  id: l.id,
  name: l.name,
  type: l.type,
  email: l.email,
  line1: l.line1,
  line2: l.line2,
  city: l.city,
  state: l.state,
  zip: l.zip,
  phone: l.phone,
  primary: l.isPrimary,
  hours: l.hours ?? null,
});
const mapHol = (h: HolidayRow): Holiday => ({
  id: h.id,
  kind: h.kind,
  date: h.date,
  ruleNth: h.ruleNth,
  ruleWeekday: h.ruleWeekday,
  ruleMonth: h.ruleMonth,
  ruleOffset: h.ruleOffset ?? 0,
  name: h.name,
  obs: h.observed,
});
// Practice profile — non-address fields live in `profile` state; the billing
// address reuses the existing `addr` + `pAddrSame` mirror.
interface Profile {
  name: string;
  legalName: string;
  dba: string;
  practiceType: string;
  npi: string;
  taxId: string;
  /** credify-platform#306 (E4): business entity type ('' = not set). */
  entityType: string;
  /** credify-platform#306 (E4): 2-letter state of incorporation ('' = not set). */
  incorporationState: string;
  taxonomy: string;
  caqhId: string;
  phone: string;
  fax: string;
  email: string;
  website: string;
  timezone: string;
  /** ehr#98: the time format clients see ('12h' | '24h'). */
  clientTimeFormat: string;
}
interface ProfileRow extends Profile {
  billing: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    zip: string;
    sameAsPrimary: boolean;
  };
}
interface BrandingRow {
  displayName: string;
  labels: Record<string, string>;
}
interface NotifRow {
  bell: boolean;
  email: boolean;
  push: boolean;
  bucketAlerts: { enabled: boolean; staleAfter: string; userIds: string[] };
  templates: { name: string; subject: string; channel: string }[];
}
interface CarrierMasked {
  phoneNumber: string;
  username: string;
  connectionId: string;
  appSid: string;
  status: string;
  hasSecret: boolean;
  secretLast4: string;
}
interface CarriersRow {
  telnyx: CarrierMasked;
  twilio: CarrierMasked;
  forwarding: {
    enabled: boolean;
    forwardTo: string;
    forwardAfter: string;
    contactFirst: string;
    contactLast: string;
    contactEmail: string;
  };
}
const DEFAULT_TZ = 'Pacific — America/Los_Angeles';
const SEED_PROFILE: Profile = {
  name: 'Credify Health Partners',
  legalName: 'Push It, Inc.',
  dba: 'Credify',
  practiceType: 'Behavioral health',
  npi: '1275938472',
  taxId: '87-2049183',
  entityType: 'S corporation',
  incorporationState: 'CA',
  taxonomy: '261QM0801X',
  caqhId: '14829375',
  phone: '(858) 227-7895',
  fax: '(858) 227-7896',
  email: 'support@credifyfast.com',
  website: 'credifyfast.com',
  timezone: DEFAULT_TZ,
  clientTimeFormat: '12h',
};
interface OrgUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
}

/* ── helpers ──────────────────────────────────────────────────────────────── */
const addrLine = (l: Location) =>
  [l.line1, l.line2, [l.city, l.state].filter(Boolean).join(', '), l.zip]
    .filter(Boolean)
    .join(' · ');

/* rail model */
const RAIL: { group: string; items: { key: string; label: string; s: string; icon: string }[] }[] =
  [
    {
      group: 'Practice',
      items: [
        {
          key: 'info',
          label: 'Practice info',
          s: 'practice name legal npi tax ein taxonomy caqh phone fax email website time zone address line 1 2 suite ste city state zip billing',
          icon: I.info,
        },
        {
          key: 'locations',
          label: 'Locations',
          s: 'locations offices address remote hub',
          icon: I.loc,
        },
        {
          // calendar#37 (Chase 10-08): RCM's payor list, between Locations and Holidays
          key: 'payors',
          label: 'Payors',
          s: 'payors payers insurance payer id medicare medi-cal medicaid commercial self-pay contract revenue cycle rcm',
          icon: I.payors,
        },
        {
          // ehr#375 (Jay 10-13): the practice's sliding scale tiers; a client's tier is set in their chart
          key: 'sliding',
          label: 'Sliding scale',
          s: 'sliding scale tiers discount fee reduced income self-pay percent dollar',
          icon: I.payors,
        },
        {
          key: 'holidays',
          label: 'Holidays',
          s: 'holidays closures company calendar days off',
          icon: I.hol,
        },
      ],
    },
    {
      group: 'Telephony',
      items: [
        {
          key: 'phone',
          label: 'Carrier accounts',
          s: 'telnyx twilio carrier credentials api key sid auth token sip forwarding forward to number rings voicemail on-call contact',
          icon: I.phone,
        },
        {
          key: 'numbers',
          label: 'Phone numbers',
          s: 'phone numbers did toll free area code port request',
          icon: I.numbers,
        },
      ],
    },
    {
      group: 'Connections',
      items: [
        {
          key: 'mail',
          label: 'Email & calendar',
          s: 'email calendar gmail outlook google mailbox video meeting rooms google meet microsoft teams zoom',
          icon: I.mail,
        },
        {
          key: 'storage',
          label: 'Document storage',
          s: 'document storage files s3 r2 cloudflare uploads',
          icon: I.storage,
        },
        {
          // ehr#331 (Taras 10-10): the practice's payment processor, Stripe via Connect
          key: 'payments',
          label: 'Payments',
          s: 'payments stripe connect card credit debit processor gateway payouts online pay',
          icon: I.card,
        },
      ],
    },
    {
      // reminders#4 (Jay 10-08): every client's opt-outs, on core consent.
      group: 'Communications',
      items: [
        {
          key: 'optouts',
          label: 'Opt-Out Center',
          s: 'opt out opt-out optout stop unsubscribe consent care marketing texts sms email calls ai listening reminders',
          icon: I.optout,
        },
      ],
    },
    {
      // ehr#309: how clients prove who they are before a QR code / web link form fills in their info.
      group: 'Client forms',
      items: [
        {
          key: 'verification',
          label: 'Client verification',
          s: 'client verification verify identity qr code web link forms portal password mrn ssn security questions',
          icon: I.verify,
        },
      ],
    },
    {
      group: 'Workspace',
      items: [
        {
          key: 'branding',
          label: 'Branding & labels',
          s: 'ehr settings organization product name labels rename wording',
          icon: I.branding,
        },
        {
          key: 'notifs',
          label: 'Notifications',
          s: 'notifications bell email desktop push bucket alerts templates assignment',
          icon: I.notifs,
        },
        {
          key: 'clinical',
          label: 'Clinical notes',
          s: 'clinical notes progress note notepilot dictation dictate microphone speech summary',
          icon: I.clinical,
        },
        {
          key: 'routing',
          label: 'Auto-routing',
          s: 'auto routing rules assignment fallback distribution',
          icon: I.routing,
        },
      ],
    },
    {
      group: 'Data',
      items: [
        {
          key: 'data',
          label: 'Backup & data',
          s: 'backup export import json reset duplicates hygiene merge scanner',
          icon: I.data,
        },
        {
          key: 'autologout',
          label: 'Auto logout',
          s: 'auto logout log out sign out timeout idle inactivity session hipaa minutes security',
          icon: I.autologout,
        },
        {
          key: 'audit',
          label: 'Audit log',
          s: 'audit log history notes changes ownership forms',
          icon: I.audit,
        },
      ],
    },
  ];

/** Small controlled switch matching the mockup's `.sw`. */
function Sw({
  checked,
  onChange,
  id,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  id?: string;
}) {
  return (
    <label className="sw">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span />
    </label>
  );
}

export function DataSettingsModal({
  onClose,
  initialSection,
}: {
  onClose: () => void;
  initialSection?: string;
}) {
  // Video meeting rooms are a practice-wide (org-level) connection, not per person,
  // so only admins/managers see and manage them (#video-isolation). Mailbox/calendar
  // stay per-user and are unaffected.
  const { user } = useSession();
  const canManageVideo = canManage(user?.role);
  // fbs#99: who may change the practice logo (the API allows admin/manager).
  const canEditPractice = canManage(user?.role);
  /* live data */
  const countsState = useFetch<Counts>(() => api.get('/contacts/counts'), []);
  const usersState = useFetch<OrgUser[]>(() => api.get('/settings/users'), []);
  const locState = useFetch<LocationRow[]>(() => api.get('/practice/locations'), []);
  const holState = useFetch<HolidayRow[]>(() => api.get('/practice/holidays'), []);
  const profileState = useFetch<ProfileRow>(() => api.get('/practice/profile'), []);
  const brandingState = useFetch<BrandingRow>(() => api.get('/practice/branding'), []);
  const notifState = useFetch<NotifRow>(() => api.get('/practice/notifications'), []);
  // calendar#46 step 5 — Clinical notes settings (NotePilot dictation, off by default).
  const clinicalState = useFetch<{
    dictationEnabled: boolean;
    serviceCodes?: Partial<Record<string, string>>;
    lateEntryHours?: number;
  }>(() => api.get('/practice/clinical'), []);
  const carriersState = useFetch<CarriersRow>(() => api.get('/practice/carriers'), []);
  // ehr#309 - Client verification (the methods a client may verify with on link / QR forms).
  const verifyState = useFetch<VerificationSettings>(() => api.get('/practice/verification'), []);

  /* navigation + search */
  const [active, setActive] = useState(initialSection ?? 'info');
  // #65 - Backup & data buttons open the real Import/Export modal (was a toast stub).
  const [ieModal, setIeModal] = useState<{ kind: ImportExportKind; autoImport?: boolean } | null>(
    null,
  );
  // Re-apply the requested section if the modal is reused while already open.
  useEffect(() => {
    if (initialSection) setActive(initialSection);
  }, [initialSection]);
  const [railQ, setRailQ] = useState('');

  /* mutable collections — Locations/Holidays are API-backed when reachable,
     otherwise they fall back to the seed (static-preview / API-down) */
  // ehr#409: empty until the practice's own list arrives (it used to flash the sample rows, "San Diego HQ"
  // with frontdesk.sd@…); the sample rows are only for when the API can't be reached (the static preview)
  const [locations, setLocations] = useState<Location[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>(SEED_HOLIDAYS);
  // Holiday "Add" date — controlled so it can use the themed DateField (#7).
  const [nhDate, setNhDate] = useState('');
  const [locLive, setLocLive] = useState(false);
  const [holLive, setHolLive] = useState(false);
  const [profile, setProfile] = useState<Profile>(SEED_PROFILE);
  const [profileLive, setProfileLive] = useState(false);
  const [displayName, setDisplayName] = useState('EHR');
  const [brandingLive, setBrandingLive] = useState(false);
  const [numbers] = useState<PhoneRow[]>(SEED_NUMBERS);
  const [pending, setPending] = useState<Pending[]>([]);
  const [mailboxes, setMailboxes] = useState<Account[]>([]);
  const [calendars, setCalendars] = useState<Account[]>([]);
  // rooms - connected Zoom accounts for meeting-room links (Meet/Teams ride SSO).
  const [rooms, setRooms] = useState<Account[]>([]);
  const [labels, setLabels] = useState<Label[]>(SEED_LABELS);
  const [templates, setTemplates] = useState<Template[]>(SEED_TEMPLATES);
  const [rules, setRules] = useState<RoutingRule[]>(SEED_RULES);

  /* toggles + small state */
  const [dirty, setDirty] = useState(false);
  // ehr#638: Practice info → Open hours, a draft of the primary location's hours. hoursEdited = changed
  // here since they were loaded (an untouched 9 to 5 starting point is never saved on its own).
  const [hoursDraft, setHoursDraft] = useState<OpenHours>(defaultOpenHours);
  const [hoursEdited, setHoursEdited] = useState(false);
  // ehr#637/#638 (Chase 10-25): Locations → a location's own Open hours, one open at a time. The
  // primary location's panel edits hoursDraft above (the same hours as Practice info); any other
  // location edits locHoursDraft.
  const [locHoursOpen, setLocHoursOpen] = useState<string | null>(null);
  const [locHoursDraft, setLocHoursDraft] = useState<OpenHours>(defaultOpenHours);
  const [locHoursEdited, setLocHoursEdited] = useState(false);
  const [locHoursBusy, setLocHoursBusy] = useState(false);
  // ehr#375: the Sliding scale pane loads on its first visit, then stays mounted (unsaved tier edits
  // survive switching panes).
  const [slidingSeen, setSlidingSeen] = useState(false);
  useEffect(() => {
    if (active === 'sliding') setSlidingSeen(true);
  }, [active]);
  // ehr#375 (the mockup's house rule): closing with unsaved tiers asks first. Cancel still discards.
  const [tiersDirty, setTiersDirty] = useState(false);
  const tiersSave = useRef<(() => Promise<boolean>) | null>(null);
  const [leaveAsk, setLeaveAsk] = useState(false);
  // ehr#465/ehr#583: an Auto logout time picked but not saved (declared here for requestClose)
  const [aloDirty, setAloDirty] = useState(false);
  // ehr#583: the Auto logout pane stays mounted after its first visit, so a picked time survives
  // switching panes until Save changes saves it (like the Sliding scale tiers).
  const [aloSeen, setAloSeen] = useState(false);
  useEffect(() => {
    if (active === 'autologout') setAloSeen(true);
  }, [active]);
  // ehr#583: an Auto logout time picked but not saved asks too, now that only Save changes saves it
  const requestClose = () => (tiersDirty || aloDirty ? setLeaveAsk(true) : onClose());
  const closeRef = useRef(requestClose);
  closeRef.current = requestClose;
  const [routeOn, setRouteOn] = useState(false);
  const [fwdOn, setFwdOn] = useState(true);
  const [fwdRings, setFwdRings] = useState('5 rings');
  const [baOn, setBaOn] = useState(true);
  const [baHours, setBaHours] = useState('24 hours');
  // Dropdowns in the add-forms below were uncontrolled <select>s read via the
  // DOM; they are controlled now so they can use the custom Dd component.
  const [nlType, setNlType] = useState('Office');
  // ehr#376: the locations a holiday is observed at; none ticked = All locations
  const [nhObs, setNhObs] = useState<string[]>([]);
  // ehr#376: Specific date (every year on that day) or Repeating date (First Monday in September)
  const [nhKind, setNhKind] = useState<'date' | 'rule'>('date');
  const [nhNth, setNhNth] = useState(1);
  const [nhWeekday, setNhWeekday] = useState(1);
  const [nhMonth, setNhMonth] = useState(9);
  const [ntCh, setNtCh] = useState('Email');
  const [rqCarrier, setRqCarrier] = useState('Telnyx');
  const [rqType, setRqType] = useState('Local');
  const [rqLoc, setRqLoc] = useState('Unassigned');
  const [fbKind, setFbKind] = useState('Leave unassigned');
  const [chBell, setChBell] = useState(true);
  const [chEmail, setChEmail] = useState(false);
  const [chPush, setChPush] = useState(false);
  // alertshub#8: this browser's notification permission, and its alert chime (per browser).
  const [pushPerm, setPushPerm] = useState<PushPermission>(pushPermission);
  const [sound, setSound] = useState(soundOn);
  const askPush = () => void requestPushPermission().then(setPushPerm);
  const [notifLive, setNotifLive] = useState(false);
  const [dictationOn, setDictationOn] = useState(false);
  // ehr#437 (CASE_MANAGEMENT_NOTES V58 21 B): each Service note's default billing code.
  const [svcCodes, setSvcCodes] = useState<Partial<Record<string, string>>>({});
  const [clinicalLive, setClinicalLive] = useState(false);
  // ehr#571 (PROGRESS_NOTE_10-20-V141): a note signed this many hours after the session asks why
  const [lateHours, setLateHours] = useState(String(LATE_ENTRY_HOURS_DEFAULT));
  const [verify, setVerify] = useState<VerificationSettings | null>(null);
  // The last saved body, so Save changes only writes (and audits) Client verification when it changed.
  const [verifySaved, setVerifySaved] = useState('');
  const [carriersLive, setCarriersLive] = useState(false);
  const [carrierHint, setCarrierHint] = useState<{ tx: string; tw: string }>({ tx: '', tw: '' });
  const [pAddrSame, setPAddrSame] = useState(true);
  const [addr, setAddr] = useState({ line1: '', line2: '', city: '', state: '', zip: '' });
  const [reveal, setReveal] = useState<Record<string, boolean>>({});
  const [txStatus, setTxStatus] = useState<'on' | 'off' | 'err'>('on');
  const [twStatus, setTwStatus] = useState<'on' | 'off' | 'err'>('off');
  const [reqOpen, setReqOpen] = useState(false);
  const [reqKind, setReqKind] = useState<'DID' | 'Number'>('DID');
  const [resetConfirm, setResetConfirm] = useState('');
  const [alerted, setAlerted] = useState<Set<string>>(new Set(['u1', 'u2']));
  const [alertedSeeded, setAlertedSeeded] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  // ehr#302: location phone / email cells holding a value that isn't valid yet
  const badLocCells = useRef(new Set<string>());
  const markLocCell = (key: string, msg: string) => (bad: boolean) => {
    if (bad) {
      badLocCells.current.add(key);
      toast(msg);
    } else badLocCells.current.delete(key);
  };
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [toastMsg, setToastMsg] = useState('');
  const [toastShow, setToastShow] = useState(false);
  const toast = (m: string) => {
    setToastMsg(m);
    setToastShow(true);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastShow(false), 2000);
  };
  const markDirty = () => setDirty(true);
  // ehr#465: an Auto logout time picked but not saved counts as unsaved, and Save changes saves it
  const aloPending = useRef<(() => Promise<boolean>) | null>(null);
  const onAloPending = (save: (() => Promise<boolean>) | null) => {
    aloPending.current = save;
    setAloDirty(Boolean(save));
  };
  // Bind a Practice-info field to `profile` state (marks dirty; Save persists it).
  const pf = (k: keyof Profile) => ({
    value: profile[k],
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const v = e.target.value;
      setProfile((p) => ({ ...p, [k]: v }));
      markDirty();
    },
  });
  // Same as pf, but for the Dd component (value-in, value-out).
  const pfDd = (k: keyof Profile) => ({
    value: profile[k],
    onChange: (v: string) => {
      setProfile((p) => ({ ...p, [k]: v }));
      markDirty();
    },
  });

  /* Locations/Holidays: adopt live data when the fetch succeeds; keep the seed
     (initial state) when the API is unreachable. */
  useEffect(() => {
    if (locState.data) {
      setLocations(locState.data.map(mapLoc));
      setLocLive(true);
    } else if (locState.error) setLocations(SEED_LOCATIONS);
  }, [locState.data, locState.error]);
  useEffect(() => {
    if (holState.data) {
      setHolidays(sortHolidays(holState.data.map(mapHol)));
      setHolLive(true);
    }
  }, [holState.data]);
  useEffect(() => {
    const d = brandingState.data;
    if (!d) return;
    setDisplayName(d.displayName || 'EHR');
    setLabels((ls) => ls.map((l) => ({ ...l, val: d.labels[l.key] ?? '' })));
    setBrandingLive(true);
  }, [brandingState.data]);
  useEffect(() => {
    const d = carriersState.data;
    if (!d) return;
    const set = (id: string, v: string) => {
      const el = rootRef.current?.querySelector<HTMLInputElement | HTMLSelectElement>(id);
      if (el) el.value = v;
    };
    set('#txNum', d.telnyx.phoneNumber);
    set('#txUser', d.telnyx.username);
    set('#txConn', d.telnyx.connectionId);
    set('#txPass', ''); // never populate the secret — only a masked hint is shown
    set('#twNum', d.twilio.phoneNumber);
    set('#twUser', d.twilio.username);
    set('#twApp', d.twilio.appSid);
    set('#twPass', '');
    set('#fwdNum', d.forwarding.forwardTo);
    setFwdRings(d.forwarding.forwardAfter || '5 rings');
    set('#fwdFirst', d.forwarding.contactFirst);
    set('#fwdLast', d.forwarding.contactLast);
    set('#fwdEmail', d.forwarding.contactEmail);
    setFwdOn(d.forwarding.enabled);
    setTxStatus(d.telnyx.status === 'on' ? 'on' : 'off');
    setTwStatus(d.twilio.status === 'on' ? 'on' : 'off');
    setCarrierHint({ tx: d.telnyx.secretLast4, tw: d.twilio.secretLast4 });
    setCarriersLive(true);
  }, [carriersState.data]);
  useEffect(() => {
    const d = verifyState.data;
    if (!d) return;
    setVerify(d);
    setVerifySaved(JSON.stringify(verificationBody(d)));
  }, [verifyState.data]);
  useEffect(() => {
    const d = notifState.data;
    if (!d) return;
    setChBell(d.bell);
    setChEmail(d.email);
    setChPush(d.push);
    setBaOn(d.bucketAlerts.enabled);
    setBaHours(d.bucketAlerts.staleAfter || '24 hours');
    setAlerted(new Set(d.bucketAlerts.userIds));
    setAlertedSeeded(true); // adopt server recipients; don't re-seed from first users
    setTemplates(d.templates.map((t) => ({ name: t.name, subj: t.subject, ch: t.channel })));
    setNotifLive(true);
  }, [notifState.data]);
  useEffect(() => {
    const d = clinicalState.data;
    if (!d) return;
    setDictationOn(Boolean(d.dictationEnabled));
    setSvcCodes(d.serviceCodes ?? {});
    setLateHours(String(d.lateEntryHours ?? LATE_ENTRY_HOURS_DEFAULT));
    setClinicalLive(true);
  }, [clinicalState.data]);
  useEffect(() => {
    const d = profileState.data;
    if (!d) return;
    setProfile({
      name: d.name,
      legalName: d.legalName,
      dba: d.dba,
      practiceType: d.practiceType || 'Behavioral health',
      npi: d.npi,
      taxId: d.taxId,
      entityType: d.entityType ?? '',
      incorporationState: d.incorporationState ?? '',
      taxonomy: d.taxonomy,
      caqhId: d.caqhId,
      phone: d.phone,
      fax: d.fax,
      email: d.email,
      website: d.website,
      timezone: d.timezone || DEFAULT_TZ,
      clientTimeFormat: d.clientTimeFormat === '24h' ? '24h' : '12h',
    });
    setPAddrSame(d.billing.sameAsPrimary);
    setAddr({
      line1: d.billing.line1,
      line2: d.billing.line2,
      city: d.billing.city,
      state: d.billing.state,
      zip: d.billing.zip,
    });
    setProfileLive(true);
  }, [profileState.data]);

  /* ESC to close + focus into the dialog */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    rootRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  /* primary location + practice address mirror */
  const primary = useMemo(() => locations.find((l) => l.primary) ?? locations[0], [locations]);
  // ehr#638: the draft follows the primary location: another primary, or its hours saved, reloads it
  // (keyed on the id + saved hours, so an unrelated location reload keeps an unsaved edit).
  const primaryHoursKey = `${primary?.id ?? ''}|${JSON.stringify(primary?.hours ?? null)}`;
  useEffect(() => {
    setHoursDraft(primary?.hours ?? defaultOpenHours());
    setHoursEdited(false);
  }, [primaryHoursKey]);
  const hoursDirty =
    locLive &&
    !!primary &&
    hoursEdited &&
    (!primary.hours || JSON.stringify(hoursDraft) !== JSON.stringify(primary.hours));
  const changeHours = (next: OpenHours) => {
    setHoursDraft(next);
    setHoursEdited(true);
    setDirty(true);
  };
  // ehr#637/#638: a non-primary location's hours panel, open with unsaved changes.
  const locHoursLoc =
    locHoursOpen && locHoursOpen !== primary?.id
      ? (locations.find((l) => l.id === locHoursOpen) ?? null)
      : null;
  const locHoursDirty =
    !!locHoursLoc &&
    locHoursEdited &&
    (!locHoursLoc.hours || JSON.stringify(locHoursDraft) !== JSON.stringify(locHoursLoc.hours));
  const changeLocHours = (next: OpenHours) => {
    setLocHoursDraft(next);
    setLocHoursEdited(true);
    setDirty(true);
  };
  /** Opens (or closes) a location's hours panel. Closing drops what wasn't saved. */
  const toggleLocHours = (l: Location) => {
    if (locHoursOpen === l.id) {
      setLocHoursOpen(null);
      if (l.id === primary?.id) {
        setHoursDraft(primary.hours ?? defaultOpenHours());
        setHoursEdited(false);
      }
      return;
    }
    setLocHoursOpen(l.id);
    setLocHoursDraft(l.hours ?? defaultOpenHours());
    setLocHoursEdited(false);
  };
  /** Saves the open location's hours (the primary's are Practice info's, so both update). */
  const saveLocHours = async (l: Location) => {
    const draft = l.id === primary?.id ? hoursDraft : locHoursDraft;
    const bad = openHoursErrors(draft);
    if (bad.length) {
      toast(bad[0]!);
      return;
    }
    let saved: OpenHours | null = draft;
    if (locLive) {
      setLocHoursBusy(true);
      try {
        const row = await api.put<LocationRow>(`/practice/locations/${l.id}/hours`, {
          hours: draft,
        });
        saved = row.hours ?? null;
      } catch (e) {
        toast(errMsg(e));
        return;
      } finally {
        setLocHoursBusy(false);
      }
    } else markDirty();
    setLocations((ls) => ls.map((x) => (x.id === l.id ? { ...x, hours: saved } : x)));
    setLocHoursOpen(null);
    setLocHoursEdited(false);
    toast(`${l.name}: open hours saved`);
  };
  const shownAddr =
    pAddrSame && primary
      ? {
          line1: primary.line1,
          line2: primary.line2,
          city: primary.city,
          state: primary.state,
          zip: primary.zip,
        }
      : addr;
  const setPrimary = async (id: string) => {
    if (locLive) {
      try {
        await api.patch(`/practice/locations/${id}/primary`);
        locState.reload();
      } catch (e) {
        toast(errMsg(e));
      }
      return;
    }
    setLocations((ls) => ls.map((l) => ({ ...l, primary: l.id === id })));
    markDirty();
  };
  const toggleAddrSame = (v: boolean) => {
    if (!v && primary)
      setAddr({
        line1: primary.line1,
        line2: primary.line2,
        city: primary.city,
        state: primary.state,
        zip: primary.zip,
      });
    setPAddrSame(v);
    markDirty();
  };
  // #63 - the practice address is read-only while it mirrors the primary location
  // ("Same as the primary location"), and users read that as "not editable". Let
  // clicking into any address field switch to a separate billing address so the
  // fields become editable in place (the toggle still works for explicit control).
  const unlockAddr = () => {
    if (pAddrSame) toggleAddrSame(false);
  };

  /* bucket-alert recipients: live users if available, else the seed */
  const alertUsers: SeedUser[] = useMemo(() => {
    const live = usersState.data;
    if (Array.isArray(live) && live.length) {
      return live.map((u) => ({
        id: u.id,
        name: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email,
        role: u.role.replace('_', ' '),
      }));
    }
    return SEED_USERS;
  }, [usersState.data]);
  useEffect(() => {
    if (alertedSeeded || !alertUsers.length) return;
    setAlerted(new Set(alertUsers.slice(0, 2).map((u) => u.id)));
    setAlertedSeeded(true);
  }, [alertUsers, alertedSeeded]);

  /* rail search filtering */
  const q = railQ.trim().toLowerCase();
  const hit = (label: string, s: string) => !q || (label + ' ' + s).toLowerCase().includes(q);
  const anyShown = RAIL.some((g) => g.items.some((it) => hit(it.label, it.s)));

  const counts = countsState.data;

  /* mutations — Locations/Holidays persist via the API when live; otherwise
     they mutate the seed locally (static-preview parity). */
  const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');
  const onSave = async () => {
    // ehr#465: the Auto logout time picked on that pane (it asks its own question first). ehr#583: a
    // bad time, a cancelled question or a failed save stops here, on that pane.
    if (aloPending.current && !(await aloPending.current())) {
      setActive('autologout');
      return;
    }
    // ehr#375 (Jay 10-30): Save changes saves unsaved sliding scale tiers too (it skipped them, so a
    // tier typed in never reached the client chart). A tier in red stops here, on that pane.
    if (tiersDirty && tiersSave.current && !(await tiersSave.current())) {
      setActive('sliding');
      return;
    }
    // ehr#302: a location phone / email left invalid is not saved silently.
    if (badLocCells.current.size) {
      setActive('locations');
      toast('Fix the location phone or email in red first');
      return;
    }
    // ehr#302: a new location typed in but not added yet is checked and added, not dropped.
    const nl = (id: string) =>
      (rootRef.current?.querySelector<HTMLInputElement>(id)?.value ?? '').trim();
    if (['#nlName', '#nlLine1', '#nlCity', '#nlZip', '#nlPhone', '#nlEmail'].some(nl)) {
      setActive('locations');
      const added = await addLocation();
      if (!added) return;
    }
    // Persist the Save-backed tabs (Practice info + Branding) where they're live;
    // list tabs (Locations/Holidays/Booking) already persisted on each mutation.
    const tasks: Promise<unknown>[] = [];
    const verifyChanged = !!verify && JSON.stringify(verificationBody(verify)) !== verifySaved;
    if (verify && verifyChanged && verificationErrors(verify).length) {
      setActive('verification');
      toast('Fix the items in red under Client verification first');
      return;
    }
    // ehr#638: the open hours are the primary location's, saved on that location.
    if (hoursDirty && primary) {
      const bad = openHoursErrors(hoursDraft);
      if (bad.length) {
        setActive('info');
        toast(bad[0]!);
        return;
      }
      const at = primary.id;
      tasks.push(
        api
          .put<LocationRow>(`/practice/locations/${at}/hours`, { hours: hoursDraft })
          .then((row) => {
            setLocations((ls) =>
              ls.map((l) => (l.id === at ? { ...l, hours: row.hours ?? null } : l)),
            );
          }),
      );
    }
    // ehr#637/#638: a location's hours panel left open with changes is saved too, not dropped.
    if (locLive && locHoursDirty && locHoursLoc) {
      const bad = openHoursErrors(locHoursDraft);
      if (bad.length) {
        setActive('locations');
        toast(`${locHoursLoc.name}: ${bad[0]!}`);
        return;
      }
      const at = locHoursLoc.id;
      tasks.push(
        api
          .put<LocationRow>(`/practice/locations/${at}/hours`, { hours: locHoursDraft })
          .then((row) => {
            setLocations((ls) =>
              ls.map((l) => (l.id === at ? { ...l, hours: row.hours ?? null } : l)),
            );
            setLocHoursOpen(null);
            setLocHoursEdited(false);
          }),
      );
    }
    if (verify && verifyChanged) {
      tasks.push(
        api
          .put<VerificationSettings>('/practice/verification', verificationBody(verify))
          .then((v) => {
            setVerify(v);
            setVerifySaved(JSON.stringify(verificationBody(v)));
          }),
      );
    }
    if (profileLive) {
      tasks.push(
        api.put('/practice/profile', {
          ...profile,
          billing: {
            line1: shownAddr.line1,
            line2: shownAddr.line2,
            city: shownAddr.city,
            state: shownAddr.state,
            zip: shownAddr.zip,
            sameAsPrimary: pAddrSame,
          },
        }),
      );
    }
    if (brandingLive) {
      const labelMap = Object.fromEntries(
        labels.filter((l) => l.val.trim()).map((l) => [l.key, l.val.trim()]),
      );
      tasks.push(api.put('/practice/branding', { displayName, labels: labelMap }));
    }
    if (clinicalLive) {
      tasks.push(
        api.put('/practice/clinical', {
          dictationEnabled: dictationOn,
          serviceCodes: svcCodes,
          ...(lateHoursOk(lateHours) ? { lateEntryHours: Number(lateHours) } : {}),
        }),
      );
    }
    if (notifLive) {
      tasks.push(
        api.put('/practice/notifications', {
          bell: chBell,
          email: chEmail,
          push: chPush,
          bucketAlerts: { enabled: baOn, staleAfter: baHours, userIds: [...alerted] },
          templates: templates.map((t) => ({ name: t.name, subject: t.subj, channel: t.ch })),
        }),
      );
    }
    if (carriersLive) {
      const g = (id: string) =>
        (rootRef.current?.querySelector<HTMLInputElement>(id)?.value ?? '').trim();
      const txSecret = g('#txPass');
      const twSecret = g('#twPass');
      tasks.push(
        api.put('/practice/carriers', {
          telnyx: {
            phoneNumber: g('#txNum'),
            username: g('#txUser'),
            connectionId: g('#txConn'),
            ...(txSecret ? { secret: txSecret } : {}),
          },
          twilio: {
            phoneNumber: g('#twNum'),
            username: g('#twUser'),
            appSid: g('#twApp'),
            ...(twSecret ? { secret: twSecret } : {}),
          },
          forwarding: {
            enabled: fwdOn,
            forwardTo: g('#fwdNum'),
            forwardAfter: fwdRings,
            contactFirst: g('#fwdFirst'),
            contactLast: g('#fwdLast'),
            contactEmail: g('#fwdEmail'),
          },
        }),
      );
    }
    if (tasks.length === 0) {
      setDirty(false);
      toast('Settings saved');
      return;
    }
    try {
      await Promise.all(tasks);
      if (notifLive) resetPracticePush(); // alertshub#8: the bell and Messages re-read Desktop push
      if (carriersLive) carriersState.reload(); // refresh masked last-4 + clear the secret field
      // ehr#458: an open note re-reads dictation on / off
      if (clinicalLive) window.dispatchEvent(new Event(CLINICAL_SETTINGS_CHANGED));
      if (brandingLive) window.dispatchEvent(new Event(BRANDING_CHANGED));
      setDirty(false);
      toast('Settings saved');
    } catch (e) {
      toast(errMsg(e));
    }
  };
  const clearLocInputs = () =>
    ['#nlName', '#nlLine1', '#nlCity', '#nlState', '#nlZip', '#nlPhone', '#nlEmail'].forEach(
      (id) => {
        const el = rootRef.current?.querySelector<HTMLInputElement>(id);
        if (el) el.value = '';
      },
    );
  const clearHolInputs = () => {
    setNhDate('');
    setNhObs([]);
    const n = rootRef.current?.querySelector<HTMLInputElement>('#nhName');
    if (n) n.value = '';
  };

  /** Adds the location typed into the form; false when it was refused (the reason is toasted). */
  const addLocation = async (): Promise<boolean> => {
    const g = (id: string) =>
      (rootRef.current?.querySelector<HTMLInputElement>(id)?.value ?? '').trim();
    if (!g('#nlName')) {
      toast('Name the location');
      rootRef.current?.querySelector<HTMLInputElement>('#nlName')?.focus();
      return false;
    }
    // ehr#302: the location's phone and front-desk email, checked like Demographics.
    if (!phoneValid(g('#nlPhone'))) {
      toast('Use a 10-digit phone number');
      rootRef.current?.querySelector<HTMLInputElement>('#nlPhone')?.focus();
      return false;
    }
    if (!emailValid(g('#nlEmail'))) {
      toast('Enter a valid front desk email, like frontdesk@yourpractice.com');
      rootRef.current?.querySelector<HTMLInputElement>('#nlEmail')?.focus();
      return false;
    }
    const payload = {
      name: g('#nlName'),
      type: nlType,
      line1: g('#nlLine1'),
      city: g('#nlCity'),
      state: g('#nlState').toUpperCase(),
      zip: g('#nlZip'),
      phone: g('#nlPhone'),
      email: g('#nlEmail'),
    };
    if (locLive) {
      try {
        await api.post('/practice/locations', payload);
        locState.reload();
        clearLocInputs();
        toast('Location added');
        return true;
      } catch (e) {
        toast(errMsg(e));
        return false;
      }
    }
    setLocations((ls) => [...ls, { id: 'loc_' + Date.now(), line2: '', ...payload }]);
    clearLocInputs();
    markDirty();
    toast('Location added');
    return true;
  };
  // ehr#302: save a location's phone or front-desk email from its row.
  const saveLocContact = async (l: Location, field: 'phone' | 'email', value: string) => {
    if (locLive) {
      try {
        await api.patch(`/practice/locations/${l.id}`, { [field]: value });
        locState.reload();
        toast(`${l.name}: ${field === 'phone' ? 'phone' : 'front desk email'} saved`);
      } catch (e) {
        toast(errMsg(e));
      }
      return;
    }
    setLocations((ls) => ls.map((x) => (x.id === l.id ? { ...x, [field]: value } : x)));
    markDirty();
  };
  // ehr#302: the X asks first, in the row (a pop-up's Escape would close this window too).
  const [confirmLoc, setConfirmLoc] = useState<string | null>(null);
  const removeLocation = async (l: Location) => {
    setConfirmLoc(null);
    if (locLive) {
      try {
        await api.del(`/practice/locations/${l.id}`);
        locState.reload();
        toast(l.name + ' removed');
      } catch (e) {
        toast(errMsg(e)); // e.g. 409 when removing the primary location
      }
      return;
    }
    setLocations((ls) => ls.filter((x) => x.id !== l.id));
    markDirty();
    toast(l.name + ' removed');
  };
  const addHoliday = async () => {
    const d = nhDate;
    const n = (rootRef.current?.querySelector<HTMLInputElement>('#nhName')?.value ?? '').trim();
    if (nhKind === 'date' && !d) {
      toast('Pick a date');
      return;
    }
    if (!n) {
      toast('Name the holiday');
      return;
    }
    const locNames = [...new Set(locations.map((l) => l.name).filter(Boolean))];
    const obs = nhObs.length && nhObs.length < locNames.length ? nhObs.join(', ') : 'All locations';
    const when =
      nhKind === 'rule'
        ? { kind: 'rule' as const, ruleNth: nhNth, ruleWeekday: nhWeekday, ruleMonth: nhMonth }
        : { kind: 'date' as const, date: d };
    if (holLive) {
      try {
        await api.post('/practice/holidays', { ...when, name: n, observed: obs });
        holState.reload();
        clearHolInputs();
        toast('Holiday added');
      } catch (e) {
        toast(errMsg(e));
      }
      return;
    }
    setHolidays((hs) => sortHolidays([...hs, { date: '', ...when, name: n, obs }]));
    clearHolInputs();
    markDirty();
    toast('Holiday added');
  };
  // ehr#376: put back any standard holiday the practice deleted.
  const addStandardHolidays = async () => {
    if (!holLive) return;
    try {
      const res = await api.post<{ added: number }>('/practice/holidays/standard', {});
      holState.reload();
      toast(
        res.added
          ? `Added ${res.added} standard holiday${res.added === 1 ? '' : 's'}`
          : 'All the standard holidays are already listed',
      );
    } catch (e) {
      toast(errMsg(e));
    }
  };
  const removeHoliday = async (h: Holiday) => {
    // ehr#376: ask first; only this holiday goes.
    const ok = await askConfirm({
      message: `Delete ${h.name}? This will remove the closure from all applicable schedules.`,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    if (holLive && h.id) {
      try {
        await api.del(`/practice/holidays/${h.id}`);
        holState.reload();
        toast(h.name + ' removed');
      } catch (e) {
        toast(errMsg(e));
      }
      return;
    }
    setHolidays((hs) => hs.filter((x) => x !== h));
    markDirty();
    toast(h.name + ' removed');
  };
  const addTemplate = () => {
    const n = (rootRef.current?.querySelector<HTMLInputElement>('#ntName')?.value ?? '').trim();
    if (!n) return toast('Name the template');
    const subj =
      (rootRef.current?.querySelector<HTMLInputElement>('#ntSubj')?.value ?? '').trim() || '—';
    const ch = ntCh;
    setTemplates((t) => [...t, { name: n, subj, ch }]);
    const ne = rootRef.current?.querySelector<HTMLInputElement>('#ntName');
    const se = rootRef.current?.querySelector<HTMLInputElement>('#ntSubj');
    if (ne) ne.value = '';
    if (se) se.value = '';
    markDirty();
    toast('Template added');
  };
  const testConn = (w: 'tx' | 'tw') => {
    const u = (
      rootRef.current?.querySelector<HTMLInputElement>('#' + w + 'User')?.value ?? ''
    ).trim();
    const p = (
      rootRef.current?.querySelector<HTMLInputElement>('#' + w + 'Pass')?.value ?? ''
    ).trim();
    const onFile = w === 'tx' ? carrierHint.tx : carrierHint.tw; // secret already stored
    if (!u || (!p && !onFile)) {
      (w === 'tx' ? setTxStatus : setTwStatus)('err');
      toast('Enter a username and password first');
      return;
    }
    (w === 'tx' ? setTxStatus : setTwStatus)('on');
    toast((w === 'tx' ? 'Telnyx' : 'Twilio') + ' credentials accepted');
  };
  // Video/calendar providers with a real OAuth grant (calendar #28 / #64). A
  // Load the caller's own mailbox/calendar connections. A single provider grant can
  // carry both capabilities, so one connection may surface in both lists.
  const loadUserConnections = () => {
    return api
      .get<{ provider: string; accountEmail: string; caps: string; status: string }[]>(
        '/video-rooms/me/connections',
      )
      .then((rows) => {
        const has = (caps: string, c: string) =>
          caps
            .split(',')
            .map((x) => x.trim())
            .includes(c);
        const mail: Account[] = [];
        const cal: Account[] = [];
        for (const r of rows) {
          if (has(r.caps, 'mail'))
            mail.push({
              email: r.accountEmail,
              provider: r.provider === 'google' ? 'Gmail' : 'Outlook',
              providerId: r.provider,
              // #501 follow-up: its sign-in expired or was removed; it needs reconnecting
              status: r.status === 'error' ? 'err' : 'ok',
            });
          if (has(r.caps, 'calendar'))
            cal.push({
              email: r.accountEmail,
              provider: r.provider === 'google' ? 'Google' : 'Outlook',
              providerId: r.provider,
              status: r.status === 'error' ? 'err' : 'ok',
            });
        }
        setMailboxes(mail);
        setCalendars(cal);
      })
      .catch(() => {
        /* leave the lists empty if the fetch fails */
      });
  };
  // Video meeting rooms are an ORG-level connection (one Meet/Teams/Zoom account per
  // practice, used to mint room links for everyone), so they come from the org list
  // (GET /video-rooms), NOT the per-user /me/connections above — the per-user path is
  // mailbox/calendar only and rejects the meet scope.
  const loadRoomConnections = () => {
    return api
      .get<{ provider: string; accountEmail: string; status: string }[]>('/video-rooms')
      .then((rows) => {
        const roomLabel = (p: string) =>
          p === 'google'
            ? 'Google Meet'
            : p === 'microsoft'
              ? 'Microsoft Teams'
              : p === 'zoom'
                ? 'Zoom'
                : p;
        setRooms(
          rows.map((r) => ({
            email: r.accountEmail || roomLabel(r.provider),
            provider: roomLabel(r.provider),
            providerId: r.provider,
            status: r.status === 'connected' ? 'ok' : r.status,
          })),
        );
      })
      .catch(() => {
        /* leave the list empty if the fetch fails */
      });
  };
  useEffect(() => {
    void loadUserConnections();
    if (canManageVideo) void loadRoomConnections();
  }, [canManageVideo]);

  // full-page navigation is required so the browser follows the provider's consent
  // screen and returns to /api/v1/video-rooms/callback with the SSO cookie; the
  // backend exchanges the code and stores the encrypted refresh token.
  //   - mail/calendar: the PER-USER connect (each user grants their own mailbox/calendar).
  //   - meet rooms: the ORG-level connect (one Meet/Teams/Zoom account per practice mints
  //     rooms for everyone; admin/manager only). The per-user path rejects the meet scope,
  //     so a room connect must use /connect/<provider>, not /me/connect.
  const connect = (k: string) => {
    const ROOM_CONNECT: Record<string, string> = {
      google_meet: 'google',
      teams: 'microsoft',
      zoom_meet: 'zoom',
    };
    if (ROOM_CONNECT[k]) {
      window.location.assign(`/api/v1/video-rooms/connect/${ROOM_CONNECT[k]}`);
      return;
    }
    const USER_CONNECT: Record<string, { provider: string; cap: string }> = {
      gmail: { provider: 'google', cap: 'mail' },
      outlook: { provider: 'microsoft', cap: 'mail' },
      google_cal: { provider: 'google', cap: 'calendar' },
      outlook_cal: { provider: 'microsoft', cap: 'calendar' },
    };
    const c = USER_CONNECT[k];
    if (c) {
      window.location.assign(`/api/v1/video-rooms/me/connect/${c.provider}?caps=${c.cap}`);
    }
  };
  // #501 follow-up: Reconnect runs the same consent as Connect (the server keeps the other scopes).
  const reconnect = (kind: string, a: Account) =>
    connect(
      kind === 'mail'
        ? a.providerId === 'google'
          ? 'gmail'
          : 'outlook'
        : a.providerId === 'google'
          ? 'google_cal'
          : 'outlook_cal',
    );
  const disconnect = (kind: string, email: string) => {
    if (kind === 'room') {
      // Org-level room connection: drop it entirely (admin/manager). No capability
      // scoping — a room connection is meet-only.
      const provider = rooms.find((a) => a.email === email)?.providerId;
      if (!provider) return;
      setRooms((r) => r.filter((a) => a.email !== email));
      api
        .del(`/video-rooms/${provider}`)
        .then(() => toast('Disconnected'))
        .catch(() => {
          toast('Could not disconnect — try again');
          void loadRoomConnections();
        });
      return;
    }
    // Mail/calendar: per-user, capability-aware. Dropping one capability keeps the token
    // if the other capability on the same provider is still connected (the server decides).
    const cap = kind === 'mail' ? 'mail' : 'calendar';
    const list = kind === 'mail' ? mailboxes : calendars;
    const setList = kind === 'mail' ? setMailboxes : setCalendars;
    const provider = list.find((a) => a.email === email)?.providerId;
    if (!provider) return;
    setList((xs) => xs.filter((a) => a.email !== email));
    api
      .del(`/video-rooms/me/${provider}?caps=${cap}`)
      .then(() => toast('Disconnected'))
      .catch(() => {
        toast('Could not disconnect — try again');
        void loadUserConnections();
      });
  };
  const submitReq = () => {
    const area = (rootRef.current?.querySelector<HTMLInputElement>('#rqArea')?.value ?? '').trim();
    const qty = Math.max(
      1,
      Math.min(
        25,
        +(rootRef.current?.querySelector<HTMLInputElement>('#rqQty')?.value ?? '1') || 1,
      ),
    );
    const type = rqType;
    if (type === 'Local' && !/^\d{3}$/.test(area)) {
      toast('A local number needs a 3-digit area code');
      rootRef.current?.querySelector<HTMLInputElement>('#rqArea')?.focus();
      return;
    }
    const carrier = rqCarrier;
    const loc = rqLoc;
    setPending((p) => [
      {
        when: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        carrier,
        kind: reqKind === 'DID' ? 'DID' : 'New number',
        type,
        area: type === 'Toll-free' ? '' : area,
        qty,
        loc,
      },
      ...p,
    ]);
    setReqOpen(false);
    const areaEl = rootRef.current?.querySelector<HTMLInputElement>('#rqArea');
    const qtyEl = rootRef.current?.querySelector<HTMLInputElement>('#rqQty');
    if (areaEl) areaEl.value = '';
    if (qtyEl) qtyEl.value = '1';
    markDirty();
    toast(
      `${qty} ${type.toLowerCase()} ${reqKind === 'DID' ? 'DID' : 'number'}${qty > 1 ? 's' : ''} requested`,
    );
  };
  const openReq = (kind: 'DID' | 'Number') => {
    setReqKind(kind);
    setReqOpen(true);
  };

  const notifSummary =
    [chBell && 'bell', chEmail && 'email', chPush && 'desktop'].filter(Boolean).join(' · ') ||
    'all channels off';
  const html = (s: string) => ({ __html: s });

  return (
    <div className="modal-backdrop ds-modal-backdrop" onClick={requestClose}>
      <div
        className="ds-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Data & Settings"
        tabIndex={-1}
        ref={rootRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ds-head">
          <div>
            <h2>Data &amp; Settings</h2>
            <p>
              {/* ehr#493: the practice's saved name (Practice info), not the sample practice's */}
              Practice details, connections, workspace configuration and data for{' '}
              <strong>{profileState.data?.name?.trim() || 'your practice'}</strong>.
            </p>
          </div>
          <button className="x" aria-label="Close" onClick={requestClose}>
            {ico(I.close)}
          </button>
        </div>

        <div className="ds-body">
          <nav className="rail" id="rail">
            <div className="rail-search">
              {ico(I.search)}
              <input
                placeholder="Search settings…"
                autoComplete="off"
                value={railQ}
                onChange={(e) => setRailQ(e.target.value)}
                aria-label="Search settings"
              />
            </div>
            {RAIL.map((g) => {
              const groupShown = g.items.some((it) => hit(it.label, it.s));
              return (
                <div key={g.group} style={{ display: 'contents' }}>
                  <span className="rail-label" hidden={!groupShown}>
                    {g.group}
                  </span>
                  {g.items.map((it) => {
                    const count =
                      it.key === 'locations'
                        ? locations.length
                        : it.key === 'holidays'
                          ? holidays.length
                          : it.key === 'numbers'
                            ? numbers.length
                            : null;
                    return (
                      <button
                        key={it.key}
                        data-tab={it.key}
                        aria-current={active === it.key}
                        hidden={!hit(it.label, it.s)}
                        onClick={() => setActive(it.key)}
                      >
                        {ico(it.icon)}
                        {it.label}
                        {count != null && <span className="pill">{count}</span>}
                      </button>
                    );
                  })}
                </div>
              );
            })}
            {!anyShown && (
              <div className="empty" style={{ fontSize: '11.5px', padding: '14px 6px' }}>
                No settings match.
              </div>
            )}
          </nav>

          <div className="ds-panes" onChange={markDirty}>
            {/* ══ PRACTICE INFO ══ */}
            <section className="pane" id="pane-info" hidden={active !== 'info'}>
              <div className="ds-info-head">
                <div>
                  <h3>Practice info</h3>
                  <p className="sub">
                    Used on invoices, client-facing forms, claim submissions and the practice header
                    on printed documents.
                  </p>
                </div>
                {/* fbs#99: the practice logo, shown on client-facing forms */}
                <PracticeLogoField canEdit={canEditPractice} />
              </div>
              <div className="sec">
                <div className="sec-head">
                  <h4>Identity</h4>
                  <span>Appears on anything a client or payer sees</span>
                </div>
                <div className="grid">
                  <div className="f">
                    <label htmlFor="pName">Practice name</label>
                    <input id="pName" {...pf('name')} />
                  </div>
                  <div className="f">
                    <label htmlFor="pLegal">Legal / billing name</label>
                    <input id="pLegal" {...pf('legalName')} />
                    <span className="help">If different from the name clients know</span>
                  </div>
                  <div className="f">
                    <label htmlFor="pDba">Doing business as</label>
                    <input id="pDba" {...pf('dba')} />
                  </div>
                  <div className="f">
                    <label htmlFor="pType">Practice type</label>
                    <Dd
                      {...pfDd('practiceType')}
                      searchable={false}
                      ariaLabel="Practice type"
                      options={[
                        'Behavioral health',
                        'Primary care',
                        'Multi-specialty',
                        'Telehealth only',
                      ].map((o) => ({ value: o, label: o }))}
                    />
                  </div>
                </div>
              </div>
              <div className="sec">
                <div className="sec-head">
                  <h4>Practice address</h4>
                  <span>Billing and correspondence address — what payers mail to</span>
                </div>
                <div className="grid">
                  <div className="f wide">
                    <label htmlFor="pAddr1">Address line 1</label>
                    <input
                      id="pAddr1"
                      value={shownAddr.line1}
                      readOnly={pAddrSame}
                      onFocus={unlockAddr}
                      onChange={(e) => setAddr((a) => ({ ...a, line1: e.target.value }))}
                    />
                  </div>
                  <div className="f">
                    <label htmlFor="pAddr2">Address line 2</label>
                    <input
                      id="pAddr2"
                      value={shownAddr.line2}
                      readOnly={pAddrSame}
                      onFocus={unlockAddr}
                      placeholder="Suite / Ste / Floor / Unit"
                      onChange={(e) => setAddr((a) => ({ ...a, line2: e.target.value }))}
                    />
                  </div>
                  <div className="f">
                    <label htmlFor="pCity">City</label>
                    <input
                      id="pCity"
                      value={shownAddr.city}
                      readOnly={pAddrSame}
                      onFocus={unlockAddr}
                      onChange={(e) => setAddr((a) => ({ ...a, city: e.target.value }))}
                    />
                  </div>
                  <div className="f">
                    <label htmlFor="pState">State</label>
                    <input
                      id="pState"
                      value={shownAddr.state}
                      readOnly={pAddrSame}
                      onFocus={unlockAddr}
                      maxLength={2}
                      onChange={(e) => setAddr((a) => ({ ...a, state: e.target.value }))}
                    />
                  </div>
                  <div className="f">
                    <label htmlFor="pZip">ZIP</label>
                    <input
                      id="pZip"
                      value={shownAddr.zip}
                      readOnly={pAddrSame}
                      onFocus={unlockAddr}
                      inputMode="numeric"
                      onChange={(e) => setAddr((a) => ({ ...a, zip: e.target.value }))}
                    />
                    <span className="help">ZIP+4 accepted</span>
                  </div>
                  <div
                    className="f wide"
                    style={{ flexDirection: 'row', alignItems: 'center', gap: '9px' }}
                  >
                    <Sw checked={pAddrSame} onChange={toggleAddrSame} />
                    <span style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>
                      Same as the primary location — <span>{primary ? primary.name : '—'}</span>.
                      Turn off to enter a separate billing address.
                    </span>
                  </div>
                </div>
              </div>
              <div className="sec">
                <div className="sec-head">
                  <h4>Identifiers</h4>
                  <span>Required for claims and credentialing</span>
                </div>
                <div className="grid">
                  <div className="f">
                    <label htmlFor="pNpi">Group NPI</label>
                    <input id="pNpi" inputMode="numeric" {...pf('npi')} />
                    <span className="help">Type 2 organizational NPI</span>
                  </div>
                  <div className="f">
                    <label htmlFor="pTax">Tax ID (EIN)</label>
                    <input id="pTax" {...pf('taxId')} />
                  </div>
                  <div className="f">
                    <label htmlFor="pEntity">Entity type</label>
                    <Dd
                      {...pfDd('entityType')}
                      searchable={false}
                      ariaLabel="Entity type"
                      options={[
                        { value: '', label: 'Not set' },
                        ...PRACTICE_ENTITY_TYPES.map((o) => ({ value: o, label: o })),
                      ]}
                    />
                  </div>
                  <div className="f">
                    <label htmlFor="pIncState">State of incorporation</label>
                    <Dd
                      {...pfDd('incorporationState')}
                      ariaLabel="State of incorporation"
                      options={[
                        { value: '', label: 'Not set' },
                        ...PRACTICE_INCORPORATION_STATES.map((o) => ({ value: o, label: o })),
                      ]}
                    />
                  </div>
                  <div className="f">
                    <label htmlFor="pTaxo">Taxonomy code</label>
                    <input id="pTaxo" {...pf('taxonomy')} />
                  </div>
                  <div className="f">
                    <label htmlFor="pCaqh">CAQH org ID</label>
                    <input id="pCaqh" {...pf('caqhId')} />
                  </div>
                </div>
              </div>
              <div className="sec">
                <div className="sec-head">
                  <h4>Contact</h4>
                  <span>Where replies and returned mail go</span>
                </div>
                <div className="grid">
                  <div className="f">
                    <label htmlFor="pPhone">Main phone</label>
                    <input id="pPhone" {...pf('phone')} />
                    <span className="help">
                      Choose the number on the Phone numbers tab to route it
                    </span>
                  </div>
                  <div className="f">
                    <label htmlFor="pFax">Fax</label>
                    <input id="pFax" {...pf('fax')} />
                  </div>
                  <div className="f">
                    <label htmlFor="pEmail">Practice email</label>
                    <input id="pEmail" type="email" {...pf('email')} />
                  </div>
                  <div className="f">
                    <label htmlFor="pWeb">Website</label>
                    <input id="pWeb" {...pf('website')} />
                  </div>
                  <div className="f">
                    <label htmlFor="pTz">Time zone</label>
                    <Dd
                      {...pfDd('timezone')}
                      searchable={false}
                      ariaLabel="Timezone"
                      options={[
                        'Pacific — America/Los_Angeles',
                        'Mountain — America/Denver',
                        'Central — America/Chicago',
                        'Eastern — America/New_York',
                      ].map((o) => ({ value: o, label: o }))}
                    />
                    <span className="help">
                      Due dates, business hours and holidays are evaluated in this zone
                    </span>
                  </div>
                  {/* ehr#98: what clients see; each person picks their own on their own profile. */}
                  <div className="f">
                    <label htmlFor="pTimeFmt">Client-facing time format</label>
                    <Dd
                      {...pfDd('clientTimeFormat')}
                      searchable={false}
                      ariaLabel="Client-facing time format"
                      options={[
                        { value: '12h', label: '12-hour (6:30 PM)' },
                        { value: '24h', label: '24-hour (18:30)' },
                      ]}
                    />
                    <span className="help">
                      Confirmations, reminders and invites clients get. Your own screens follow the
                      Time format on your profile (your name, top right → My profile).
                    </span>
                  </div>
                  <div className="f">
                    <label htmlFor="pPrimary">Primary location</label>
                    <Dd
                      value={primary?.id ?? ''}
                      onChange={setPrimary}
                      ariaLabel="Primary location"
                      options={locations.map((l) => ({ value: l.id, label: l.name }))}
                    />
                  </div>
                </div>
              </div>
              {/* ehr#638: the primary location's open hours */}
              <PracticeHoursSection
                hours={hoursDraft}
                onChange={changeHours}
                locationName={primary ? primary.name : null}
                saved={!!primary?.hours}
                canEdit={canEditPractice}
              />
            </section>

            {/* ══ LOCATIONS ══ */}
            <section className="pane" id="pane-locations" hidden={active !== 'locations'}>
              <h3>Locations</h3>
              <p className="sub">
                Offices and remote hubs. A location can be attached to a user, an appointment, or a
                phone number, and the primary one appears on claims by default. The Calendar and
                each client&apos;s primary location use this list.
              </p>
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Address</th>
                      <th>Phone</th>
                      <th>Front desk email</th>
                      <th>Open hours</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {locations.map((l) => (
                      <Fragment key={l.id}>
                        <tr className={locHoursOpen === l.id ? 'ds-loc-open' : undefined}>
                          <td>
                            <strong>{l.name}</strong>{' '}
                            {l.primary && <span className="badge">Primary</span>}
                          </td>
                          <td>
                            <span className={`badge ${l.type === 'Office' ? 'blue' : 'grey'}`}>
                              {l.type}
                            </span>
                          </td>
                          <td className="muted">{addrLine(l)}</td>
                          {/* ehr#302: phone and front-desk email are edited in place. */}
                          <td className="num">
                            <LocContactCell
                              value={l.phone}
                              kind="phone"
                              label={`${l.name} phone`}
                              disabled={!canManage(user?.role)}
                              onSave={(v) => saveLocContact(l, 'phone', v)}
                              onInvalid={markLocCell(
                                `${l.id}.phone`,
                                'Use a 10-digit phone number',
                              )}
                            />
                          </td>
                          <td className="muted">
                            <LocContactCell
                              value={l.email}
                              kind="email"
                              label={`${l.name} front desk email`}
                              disabled={!canManage(user?.role)}
                              onSave={(v) => saveLocContact(l, 'email', v)}
                              onInvalid={markLocCell(
                                `${l.id}.email`,
                                'Enter a valid front desk email, like frontdesk@yourpractice.com',
                              )}
                            />
                          </td>
                          {/* ehr#637/#638: each location's own open hours */}
                          <td className="ds-loc-hours-cell">
                            <span className={l.hours ? '' : 'muted'}>
                              {openHoursSummary(l.hours)}
                            </span>
                            <button
                              className="btn sm"
                              aria-expanded={locHoursOpen === l.id}
                              aria-label={`${locHoursOpen === l.id ? 'Close' : canEditPractice ? 'Edit' : 'View'} open hours of ${l.name}`}
                              onClick={() => toggleLocHours(l)}
                            >
                              {locHoursOpen === l.id
                                ? 'Close'
                                : canEditPractice
                                  ? l.hours
                                    ? 'Edit hours'
                                    : 'Set hours'
                                  : 'View hours'}
                            </button>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {!l.primary &&
                              (confirmLoc === l.id ? (
                                <span
                                  className="row-confirm"
                                  role="group"
                                  aria-label={`Remove ${l.name}?`}
                                >
                                  Remove {l.name}?
                                  <button className="btn sm" onClick={() => setConfirmLoc(null)}>
                                    Keep
                                  </button>
                                  <button
                                    className="btn sm danger"
                                    onClick={() => removeLocation(l)}
                                  >
                                    Remove
                                  </button>
                                </span>
                              ) : (
                                <button
                                  className="row-x"
                                  aria-label={`Remove ${l.name}`}
                                  onClick={() => setConfirmLoc(l.id)}
                                >
                                  {ico(I.x, 2.4)}
                                </button>
                              ))}
                          </td>
                        </tr>
                        {locHoursOpen === l.id && (
                          <tr className="ds-loc-hours-row">
                            <td colSpan={7}>
                              <PracticeHoursSection
                                variant="location"
                                hours={l.id === primary?.id ? hoursDraft : locHoursDraft}
                                onChange={l.id === primary?.id ? changeHours : changeLocHours}
                                locationName={l.name}
                                isPrimary={l.id === primary?.id}
                                saved={!!l.hours}
                                canEdit={canEditPractice}
                                footer={
                                  canEditPractice && (
                                    <div className="ds-hours-actions">
                                      <button className="btn sm" onClick={() => toggleLocHours(l)}>
                                        Cancel
                                      </button>
                                      <button
                                        className="btn sm primary"
                                        disabled={
                                          locHoursBusy ||
                                          openHoursErrors(
                                            l.id === primary?.id ? hoursDraft : locHoursDraft,
                                          ).length > 0
                                        }
                                        onClick={() => saveLocHours(l)}
                                      >
                                        Save hours
                                      </button>
                                    </div>
                                  )
                                }
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="addbar">
                <div className="f" style={{ flex: '1 1 150px' }}>
                  <label>Name</label>
                  <input id="nlName" placeholder="e.g. Sacramento" />
                </div>
                <div className="f" style={{ flex: '0 0 116px' }}>
                  <label>Type</label>
                  <Dd
                    value={nlType}
                    onChange={setNlType}
                    searchable={false}
                    ariaLabel="Location type"
                    options={['Office', 'Remote', 'Satellite'].map((o) => ({ value: o, label: o }))}
                  />
                </div>
                <div className="f" style={{ flex: '1 1 160px' }}>
                  <label>Address line 1</label>
                  <input id="nlLine1" placeholder="Street address" />
                </div>
                <div className="f" style={{ flex: '0 0 108px' }}>
                  <label>City</label>
                  <input id="nlCity" />
                </div>
                <div className="f" style={{ flex: '0 0 60px' }}>
                  <label>State</label>
                  <input id="nlState" maxLength={2} />
                </div>
                <div className="f" style={{ flex: '0 0 80px' }}>
                  <label>ZIP</label>
                  <input id="nlZip" />
                </div>
                <div className="f" style={{ flex: '0 0 140px' }}>
                  <label>Phone</label>
                  <input
                    id="nlPhone"
                    type="tel"
                    placeholder="(___) ___-____"
                    onInput={(e) => {
                      e.currentTarget.value = maskPhone(e.currentTarget.value);
                    }}
                  />
                </div>
                <div className="f" style={{ flex: '1 1 180px' }}>
                  <label>Front desk email</label>
                  <input id="nlEmail" type="email" placeholder="frontdesk@yourpractice.com" />
                </div>
                <button className="btn primary" onClick={addLocation}>
                  Add location
                </button>
              </div>
            </section>

            {/* ══ PAYORS (calendar#37) ══ */}
            <section className="pane" id="pane-payors" hidden={active !== 'payors'}>
              <h3>Payors</h3>
              <p className="sub">
                The insurance payors your practice bills. This is Revenue Cycle&apos;s payor list:
                the Calendar, each client&apos;s Insurance / Payor and claims all use it. Changes
                save right away.
              </p>
              {active === 'payors' && <PayorsSettings />}
            </section>

            {/* ══ SLIDING SCALE (ehr#375) ══ */}
            <section className="pane" id="pane-sliding" hidden={active !== 'sliding'}>
              <h3>Sliding scale tiers</h3>
              <p className="sub">
                Your practice&apos;s sliding scale tiers. Picking a tier in a client&apos;s chart
                (Profile → Insurance / Payor) fills in its discount, and it can be changed there for
                that client. The discount comes off what the client owes after insurance, never what
                a payor owes.
              </p>
              {slidingSeen && (
                <SlidingTiersSettings toast={toast} onDirty={setTiersDirty} saveRef={tiersSave} />
              )}
            </section>

            {/* ══ HOLIDAYS ══ */}
            <section className="pane" id="pane-holidays" hidden={active !== 'holidays'}>
              <h3>Company holidays</h3>
              <p className="sub">
                Org-wide closures. These apply to everyone, so you don't have to mark each person
                off individually — availability, due-date math and scheduling all skip these days.
                They show as closures in the Calendar.
              </p>
              {holLive && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '0 0 8px' }}>
                  <button type="button" className="btn" onClick={() => void addStandardHolidays()}>
                    Add standard holidays
                  </button>
                </div>
              )}
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th style={{ width: '240px' }}>Date</th>
                      <th>Holiday</th>
                      <th style={{ width: '150px' }}>Observed</th>
                      <th style={{ width: '96px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {holidays.length ? (
                      holidays.map((h) => (
                        <tr key={h.id ?? h.date + h.name}>
                          <td>{holidayLabel(h)}</td>
                          <td>
                            <strong>{h.name}</strong>
                          </td>
                          <td className="muted">{h.obs || 'All locations'}</td>
                          <td style={{ textAlign: 'right' }}>
                            <button
                              type="button"
                              className="hol-del"
                              aria-label={`Delete ${h.name}`}
                              onClick={() => void removeHoliday(h)}
                            >
                              {ico(I.trash, 2)} Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="empty">
                          No company holidays added yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="addbar hol-add">
                <div className="f" style={{ flex: '0 0 auto' }}>
                  <label id="nhKindLbl">Holiday type</label>
                  <div className="hol-type" role="radiogroup" aria-labelledby="nhKindLbl">
                    {(
                      [
                        ['date', 'Specific date'],
                        ['rule', 'Repeating date'],
                      ] as const
                    ).map(([k, label]) => (
                      <label key={k} className={`hol-type-opt${nhKind === k ? ' on' : ''}`}>
                        <input
                          type="radio"
                          name="nhKind"
                          value={k}
                          checked={nhKind === k}
                          onChange={() => setNhKind(k)}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="f" style={{ flex: '1 1 200px' }}>
                  <label>Holiday name</label>
                  <input id="nhName" placeholder="e.g. Independence Day" />
                </div>
                <div className="f" style={{ flex: '0 0 200px' }}>
                  <label>Observed</label>
                  {/* ehr#376 (Chase 10-13): pick one location, several, or none = All locations */}
                  <MultiDd
                    values={nhObs}
                    onChange={setNhObs}
                    placeholder="All locations"
                    searchable={false}
                    allRow
                    maxLabel={2}
                    emptyText="No locations yet"
                    ariaLabel="Observed at"
                    floating
                    options={[...new Set(locations.map((l) => l.name))]
                      .filter(Boolean)
                      .map((o) => ({ value: o, label: o }))}
                  />
                </div>
                <button className="btn primary" onClick={addHoliday}>
                  Add holiday
                </button>
                {nhKind === 'date' ? (
                  <div className="hol-when">
                    <div className="f" style={{ flex: '0 0 165px' }}>
                      <label>Date</label>
                      <DateField value={nhDate} onChange={setNhDate} id="nhDate" />
                    </div>
                    {nhDate && (
                      <p className="hol-preview">
                        Repeats every year: {holidayLabel({ kind: 'date', date: nhDate })}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="hol-when">
                    <div className="hol-when-head">{ico(I.hol, 2)} Add recurring holiday</div>
                    <div className="hol-rule">
                      <div className="f">
                        <label>Occurrence</label>
                        <Dd
                          value={String(nhNth)}
                          onChange={(v) => setNhNth(Number(v))}
                          searchable={false}
                          ariaLabel="Occurrence"
                          options={HOLIDAY_NTH_LABELS.map((l, i) => ({
                            value: String(i + 1),
                            label: l,
                          }))}
                        />
                      </div>
                      <div className="f">
                        <label>Weekday</label>
                        <Dd
                          value={String(nhWeekday)}
                          onChange={(v) => setNhWeekday(Number(v))}
                          searchable={false}
                          ariaLabel="Weekday"
                          options={WEEKDAY_ORDER.map((d) => ({
                            value: String(d),
                            label: WEEKDAY_NAMES[d]!,
                          }))}
                        />
                      </div>
                      <div className="f">
                        <label>Month</label>
                        <Dd
                          value={String(nhMonth)}
                          onChange={(v) => setNhMonth(Number(v))}
                          searchable={false}
                          ariaLabel="Month"
                          options={MONTH_NAMES.map((m, i) => ({ value: String(i + 1), label: m }))}
                        />
                      </div>
                    </div>
                    <p className="hol-preview">
                      Repeats every year:{' '}
                      {holidayLabel({
                        kind: 'rule',
                        date: '',
                        ruleNth: nhNth,
                        ruleWeekday: nhWeekday,
                        ruleMonth: nhMonth,
                      })}
                    </p>
                  </div>
                )}
              </div>
            </section>

            {/* ══ CARRIER ACCOUNTS ══ */}
            <section className="pane" id="pane-phone" hidden={active !== 'phone'}>
              <h3>Carrier accounts</h3>
              <p className="sub">
                Credentials for the carriers that own your numbers. Telnyx and Twilio can both be
                connected — numbers from either appear together on the Phone numbers tab.
              </p>
              <div className="note" style={{ margin: '0 0 16px' }}>
                <b>Set up by Credify support for now.</b> To change a number or the credentials,
                email support@credifyfast.com.
              </div>

              {/* ehr#298: greyed out until there's a platform admin page; only support changes
                  these (the API keeps them for everyone else). */}
              <fieldset className="prov-locked" disabled title="Set up by Credify support for now">
                <div className="prov">
                  <div className="prov-head">
                    <span className="prov-mark tx">T</span>
                    <div>
                      <div className="nm">Telnyx</div>
                      <div className="sb">SIP trunking · DIDs · SMS</div>
                    </div>
                    <div className="right">
                      <span className={`status ${txStatus}`}>
                        <i />
                        {txStatus === 'on'
                          ? 'Connected'
                          : txStatus === 'err'
                            ? 'Missing credentials'
                            : 'Not connected'}
                      </span>
                      <button className="btn sm" onClick={() => testConn('tx')}>
                        Test connection
                      </button>
                    </div>
                  </div>
                  <div className="prov-body">
                    <div className="grid">
                      <div className="f">
                        <label htmlFor="txNum">Phone number</label>
                        <input id="txNum" placeholder="+1 …" />
                        <span className="help">Default outbound caller ID</span>
                      </div>
                      <div className="f">
                        <label htmlFor="txUser">
                          Username{' '}
                          <span style={{ fontWeight: 400, color: 'var(--text-muted-2)' }}>
                            (API key ID)
                          </span>
                        </label>
                        <input id="txUser" placeholder="KEY…" />
                      </div>
                      <div className="f">
                        <label htmlFor="txPass">
                          Password{' '}
                          <span style={{ fontWeight: 400, color: 'var(--text-muted-2)' }}>
                            (API secret)
                          </span>
                        </label>
                        <span className="secret">
                          <input
                            id="txPass"
                            type={reveal.txPass ? 'text' : 'password'}
                            placeholder="••••••••••••••••"
                          />
                          <button
                            className="eye"
                            aria-label={reveal.txPass ? 'Hide' : 'Reveal'}
                            onClick={() => setReveal((r) => ({ ...r, txPass: !r.txPass }))}
                          >
                            {ico(reveal.txPass ? I.eyeOn : I.eyeOff)}
                          </button>
                        </span>
                        {carrierHint.tx && (
                          <span className="help">
                            Secret on file · ····{carrierHint.tx} — enter a new value to replace
                          </span>
                        )}
                      </div>
                      <div className="f">
                        <label htmlFor="txConn">Connection ID</label>
                        <input id="txConn" />
                        <span className="help">The SIP connection new DIDs get assigned to</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="prov">
                  <div className="prov-head">
                    <span className="prov-mark tw">T</span>
                    <div>
                      <div className="nm">Twilio</div>
                      <div className="sb">Voice · SMS · number provisioning</div>
                    </div>
                    <div className="right">
                      <span className={`status ${twStatus}`}>
                        <i />
                        {twStatus === 'on'
                          ? 'Connected'
                          : twStatus === 'err'
                            ? 'Missing credentials'
                            : 'Not connected'}
                      </span>
                      <button className="btn sm" onClick={() => testConn('tw')}>
                        Test connection
                      </button>
                    </div>
                  </div>
                  <div className="prov-body">
                    <div className="grid">
                      <div className="f">
                        <label htmlFor="twNum">Phone number</label>
                        <input id="twNum" placeholder="+1 …" />
                        <span className="help">Default outbound caller ID</span>
                      </div>
                      <div className="f">
                        <label htmlFor="twUser">
                          Username{' '}
                          <span style={{ fontWeight: 400, color: 'var(--text-muted-2)' }}>
                            (Account SID)
                          </span>
                        </label>
                        <input id="twUser" placeholder="AC…" />
                      </div>
                      <div className="f">
                        <label htmlFor="twPass">
                          Password{' '}
                          <span style={{ fontWeight: 400, color: 'var(--text-muted-2)' }}>
                            (Auth token)
                          </span>
                        </label>
                        <span className="secret">
                          <input
                            id="twPass"
                            type={reveal.twPass ? 'text' : 'password'}
                            placeholder="••••••••••••••••"
                          />
                          <button
                            className="eye"
                            aria-label={reveal.twPass ? 'Hide' : 'Reveal'}
                            onClick={() => setReveal((r) => ({ ...r, twPass: !r.twPass }))}
                          >
                            {ico(reveal.twPass ? I.eyeOn : I.eyeOff)}
                          </button>
                        </span>
                        {carrierHint.tw && (
                          <span className="help">
                            Secret on file · ····{carrierHint.tw} — enter a new value to replace
                          </span>
                        )}
                      </div>
                      <div className="f">
                        <label htmlFor="twApp">TwiML app SID</label>
                        <input id="twApp" placeholder="AP…" />
                        <span className="help">Optional — only if calls route through an app</span>
                      </div>
                    </div>
                  </div>
                </div>
              </fieldset>

              <div className="sec">
                <div className="sec-head">
                  <h4>Call forwarding</h4>
                  <span>Where calls go when nobody picks up here</span>
                </div>
                <div className="tog" style={{ paddingTop: 0 }}>
                  <div className="tog-txt">
                    <div className="t">Forward unanswered calls</div>
                    <div className="d">
                      {fwdOn
                        ? 'On — calls ring here first, then forward.'
                        : 'Off — unanswered calls go to voicemail instead of forwarding.'}
                    </div>
                  </div>
                  <Sw
                    checked={fwdOn}
                    onChange={(v) => {
                      setFwdOn(v);
                      markDirty();
                    }}
                  />
                </div>
                <div className="grid" style={{ marginTop: '14px' }}>
                  <div className="f">
                    <label htmlFor="fwdNum">Forward to number</label>
                    <input id="fwdNum" defaultValue="+1 (619) 555-0114" disabled={!fwdOn} />
                    <span className="help">
                      Answering service, on-call mobile, or another office
                    </span>
                  </div>
                  <div className="f">
                    <label htmlFor="fwdRings">Forward after</label>
                    <Dd
                      value={fwdRings}
                      onChange={setFwdRings}
                      disabled={!fwdOn}
                      searchable={false}
                      ariaLabel="Forward after"
                      options={['3 rings', '5 rings', '8 rings', 'Immediately'].map((o) => ({
                        value: o,
                        label: o,
                      }))}
                    />
                  </div>
                </div>
                <div className="sec-head" style={{ marginTop: '18px' }}>
                  <h4>Forwarding contact</h4>
                  <span>Who is on the other end — used on caller ID and voicemail notices</span>
                </div>
                <div className="grid">
                  <div className="f">
                    <label htmlFor="fwdFirst">First name</label>
                    <input id="fwdFirst" defaultValue="Maya" disabled={!fwdOn} />
                  </div>
                  <div className="f">
                    <label htmlFor="fwdLast">Last name</label>
                    <input id="fwdLast" defaultValue="Okonkwo" disabled={!fwdOn} />
                  </div>
                  <div className="f">
                    <label htmlFor="fwdEmail">Email</label>
                    <input
                      id="fwdEmail"
                      type="email"
                      defaultValue="maya@credifyfast.com"
                      disabled={!fwdOn}
                    />
                    <span className="help">Missed-call and voicemail notifications go here</span>
                  </div>
                </div>
              </div>
            </section>

            {/* ══ PHONE NUMBERS ══ */}
            <section className="pane" id="pane-numbers" hidden={active !== 'numbers'}>
              <h3>Phone numbers</h3>
              <p className="sub">
                Every number on the account, whichever carrier it came from, and where each one
                rings.
              </p>
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Number</th>
                      <th>Carrier</th>
                      <th>Type</th>
                      <th>Assigned to</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {numbers.map((n) => (
                      <tr key={n.num}>
                        <td className="num">
                          <strong>{n.num}</strong> {n.main && <span className="badge">Main</span>}
                        </td>
                        <td>{n.carrier}</td>
                        <td className="muted">{n.type}</td>
                        <td className="muted">{n.loc}</td>
                        <td>
                          <span className={`badge ${n.status === 'Active' ? '' : 'amber'}`}>
                            {n.status}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button className="btn sm ghost">Edit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="btn-row">
                <button className="btn primary" onClick={() => openReq('DID')}>
                  {ico(I.plus)}Request a DID
                </button>
                <button className="btn" onClick={() => openReq('Number')}>
                  {ico(I.search)}Request a phone number
                </button>
                <span className="foot-note" style={{ marginLeft: '4px' }}>
                  A DID attaches to your existing trunk · a phone number is provisioned fresh from
                  carrier inventory
                </span>
              </div>
              {reqOpen && (
                <div className="reqpanel">
                  <h5>{reqKind === 'DID' ? 'Request a DID' : 'Request a phone number'}</h5>
                  <p>
                    {reqKind === 'DID'
                      ? 'Attaches a direct inward dial number to your existing SIP trunk. Use this when the trunk is live and you just need another number pointing at it.'
                      : "Provisions a brand-new number from the carrier's inventory, including any porting paperwork. Use this when you need a number the practice does not own yet."}
                  </p>
                  <div className="grid">
                    <div className="f">
                      <label htmlFor="rqCarrier">Carrier</label>
                      <Dd
                        value={rqCarrier}
                        onChange={setRqCarrier}
                        searchable={false}
                        ariaLabel="Carrier"
                        options={['Telnyx', 'Twilio'].map((o) => ({ value: o, label: o }))}
                      />
                    </div>
                    <div className="f">
                      <label htmlFor="rqType">Number type</label>
                      <Dd
                        value={rqType}
                        onChange={setRqType}
                        searchable={false}
                        ariaLabel="Number type"
                        options={['Local', 'Toll-free'].map((o) => ({ value: o, label: o }))}
                      />
                    </div>
                    <div className="f">
                      <label htmlFor="rqArea">Area code</label>
                      <input id="rqArea" placeholder="e.g. 619" maxLength={3} inputMode="numeric" />
                    </div>
                    <div className="f">
                      <label htmlFor="rqQty">How many</label>
                      <input id="rqQty" type="number" min={1} max={25} defaultValue={1} />
                    </div>
                    <div className="f">
                      <label htmlFor="rqLoc">Assign to location</label>
                      <Dd
                        value={rqLoc}
                        onChange={setRqLoc}
                        searchable={false}
                        ariaLabel="Location"
                        options={[
                          { value: 'Unassigned', label: 'Unassigned' },
                          ...locations.map((l) => ({ value: l.name, label: l.name })),
                        ]}
                      />
                    </div>
                    <div className="f">
                      <label htmlFor="rqNote">Note for the carrier</label>
                      <input id="rqNote" placeholder="Optional" />
                    </div>
                  </div>
                  <div className="btn-row">
                    <button className="btn primary" onClick={submitReq}>
                      Submit request
                    </button>
                    <button className="btn ghost" onClick={() => setReqOpen(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {pending.length > 0 && (
                <div className="sec">
                  <div className="sec-head">
                    <h4>Pending requests</h4>
                    <span>Carrier provisioning usually lands within one business day</span>
                  </div>
                  <div className="tbl-wrap">
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th>Requested</th>
                          <th>Carrier</th>
                          <th>Type</th>
                          <th>Area code</th>
                          <th>Qty</th>
                          <th>Location</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pending.map((p, i) => (
                          <tr key={i}>
                            <td className="muted">{p.when}</td>
                            <td>{p.carrier}</td>
                            <td className="muted">
                              {p.kind} · {p.type}
                            </td>
                            <td className="num">{p.area || 'any'}</td>
                            <td className="num">{p.qty}</td>
                            <td className="muted">{p.loc}</td>
                            <td>
                              <span className="badge amber">Submitted</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </section>

            {/* ══ EMAIL & CALENDAR ══ */}
            <section className="pane" id="pane-mail" hidden={active !== 'mail'}>
              <h3>Email &amp; calendar</h3>
              <p className="sub">
                Each person connects their own Google or Microsoft mailbox and calendar here. Admins
                and managers choose below whether appointments go on those calendars, which
                admin&apos;s calendar is the practice calendar (every appointment), and which
                admin&apos;s mailbox sends the practice&apos;s email.
              </p>

              <div className="sec">
                <div className="sec-head">
                  <h4>Mailboxes</h4>
                  <span>{mailboxes.length} connected</span>
                </div>
                <div>
                  {mailboxes.length ? (
                    mailboxes.map((a) => (
                      <AcctCard
                        key={a.email}
                        a={a}
                        kind="mail"
                        onDisconnect={disconnect}
                        onReconnect={reconnect}
                      />
                    ))
                  ) : (
                    <div
                      className="empty"
                      style={{ border: '1px dashed var(--border)', borderRadius: '10px' }}
                    >
                      No mailbox connected — outgoing email uses handoff.
                    </div>
                  )}
                </div>
                <div className="btn-row">
                  <button className="btn" onClick={() => connect('gmail')}>
                    <span
                      className="prov-mark g"
                      style={{
                        width: '18px',
                        height: '18px',
                        fontSize: '10px',
                        borderRadius: '5px',
                      }}
                    >
                      G
                    </span>
                    Connect Gmail
                  </button>
                  <button className="btn" onClick={() => connect('outlook')}>
                    <span
                      className="prov-mark o"
                      style={{
                        width: '18px',
                        height: '18px',
                        fontSize: '10px',
                        borderRadius: '5px',
                      }}
                    >
                      O
                    </span>
                    Connect Outlook
                  </button>
                </div>
              </div>

              <div className="sec">
                <div className="sec-head">
                  <h4>Calendars</h4>
                  <span>{calendars.length} connected</span>
                </div>
                <div>
                  {calendars.length ? (
                    calendars.map((a) => (
                      <AcctCard
                        key={a.email + a.provider}
                        a={a}
                        kind="cal"
                        onDisconnect={disconnect}
                        onReconnect={reconnect}
                      />
                    ))
                  ) : (
                    <div
                      className="empty"
                      style={{ border: '1px dashed var(--border)', borderRadius: '10px' }}
                    >
                      No calendar connected.
                    </div>
                  )}
                </div>
                <div className="btn-row">
                  <button className="btn" onClick={() => connect('google_cal')}>
                    <span
                      className="prov-mark g"
                      style={{
                        width: '18px',
                        height: '18px',
                        fontSize: '10px',
                        borderRadius: '5px',
                      }}
                    >
                      G
                    </span>
                    Connect Google Calendar
                  </button>
                  <button className="btn" onClick={() => connect('outlook_cal')}>
                    <span
                      className="prov-mark o"
                      style={{
                        width: '18px',
                        height: '18px',
                        fontSize: '10px',
                        borderRadius: '5px',
                      }}
                    >
                      O
                    </span>
                    Connect Outlook Calendar
                  </button>
                </div>
              </div>

              {/* ehr#83: the practice's calendar-sync and mailbox-email switches */}
              {canManageVideo && active === 'mail' && <SyncSettings />}

              {canManageVideo && (
                <div className="sec">
                  <div className="sec-head">
                    <h4>Video meeting rooms</h4>
                    <span>{rooms.length} connected</span>
                  </div>
                  <p className="note" style={{ marginTop: 0, marginBottom: '10px' }}>
                    Practice-wide: one shared Meet, Teams or Zoom connection per provider for the
                    whole practice, used to put one-click room links on appointments. Managed here
                    by admins and managers, not per person.
                  </p>
                  <div>
                    {rooms.length ? (
                      rooms.map((a) => (
                        <AcctCard
                          key={a.email + a.provider}
                          a={a}
                          kind="room"
                          onDisconnect={disconnect}
                        />
                      ))
                    ) : (
                      <div
                        className="empty"
                        style={{ border: '1px dashed var(--border)', borderRadius: '10px' }}
                      >
                        No video provider connected.
                      </div>
                    )}
                  </div>
                  <div className="btn-row">
                    <button className="btn" onClick={() => connect('google_meet')}>
                      <span
                        className="prov-mark"
                        style={{
                          width: '18px',
                          height: '18px',
                          fontSize: '10px',
                          borderRadius: '5px',
                          background: '#00897b',
                          color: '#fff',
                        }}
                      >
                        M
                      </span>
                      Connect Google Meet
                    </button>
                    <button className="btn" onClick={() => connect('teams')}>
                      <span
                        className="prov-mark"
                        style={{
                          width: '18px',
                          height: '18px',
                          fontSize: '10px',
                          borderRadius: '5px',
                          background: '#4b53bc',
                          color: '#fff',
                        }}
                      >
                        T
                      </span>
                      Connect Microsoft Teams
                    </button>
                    <button className="btn" onClick={() => connect('zoom_meet')}>
                      <span
                        className="prov-mark"
                        style={{
                          width: '18px',
                          height: '18px',
                          fontSize: '10px',
                          borderRadius: '5px',
                          background: '#2d8cff',
                          color: '#fff',
                        }}
                      >
                        Z
                      </span>
                      Connect Zoom
                    </button>
                  </div>
                </div>
              )}
            </section>

            {/* ══ DOCUMENT STORAGE ══ */}
            {/* ══ PAYMENTS (ehr#331) ══ */}
            <section className="pane" id="pane-payments" hidden={active !== 'payments'}>
              <h3>Payments</h3>
              <p className="sub">
                The payment processor your practice takes client payments through. Each practice
                connects its own account; money goes straight to it.
              </p>
              {active === 'payments' && <PaymentsSettings />}
            </section>

            <section className="pane" id="pane-storage" hidden={active !== 'storage'}>
              <h3>Document storage</h3>
              <p className="sub">
                Where uploaded files live — chart documents, signed forms, insurance cards and task
                attachments.
              </p>
              <div className="prov">
                <div className="prov-head">
                  <span className="prov-mark s">B</span>
                  <div>
                    <div className="nm">Browser storage</div>
                    <div className="sb">Current — files live in this browser only</div>
                  </div>
                  <div className="right">
                    <span className="status wait">
                      <i />
                      Prototype
                    </span>
                  </div>
                </div>
                <div className="prov-body">
                  <p
                    style={{
                      margin: 0,
                      fontSize: '12.5px',
                      color: 'var(--text-muted)',
                      lineHeight: 1.55,
                    }}
                  >
                    Files are held in local storage alongside the rest of the workspace. They don't
                    sync between machines, they count against the browser's storage quota, and
                    clearing site data deletes them. Fine for a prototype, not for charts.
                  </p>
                </div>
              </div>
              <div className="prov">
                <div className="prov-head">
                  <span className="prov-mark s" style={{ background: '#e07a2f' }}>
                    S
                  </span>
                  <div>
                    <div className="nm">S3 / Cloudflare R2</div>
                    <div className="sb">Object storage with presigned uploads</div>
                  </div>
                  <div className="right">
                    <span className="status off">
                      <i />
                      Not connected
                    </span>
                  </div>
                </div>
                <div className="prov-body">
                  <div className="grid">
                    <div className="f">
                      <label htmlFor="s3Bucket">Bucket</label>
                      <input id="s3Bucket" placeholder="credify-charts" />
                    </div>
                    <div className="f">
                      <label htmlFor="s3Region">Region</label>
                      <input id="s3Region" placeholder="us-west-2" />
                    </div>
                    <div className="f">
                      <label htmlFor="s3Key">Access key ID</label>
                      <input id="s3Key" placeholder="AKIA…" />
                    </div>
                    <div className="f">
                      <label htmlFor="s3Secret">Secret access key</label>
                      <span className="secret">
                        <input
                          id="s3Secret"
                          type={reveal.s3Secret ? 'text' : 'password'}
                          placeholder="••••••••••••••••"
                        />
                        <button
                          className="eye"
                          aria-label={reveal.s3Secret ? 'Hide' : 'Reveal'}
                          onClick={() => setReveal((r) => ({ ...r, s3Secret: !r.s3Secret }))}
                        >
                          {ico(reveal.s3Secret ? I.eyeOn : I.eyeOff)}
                        </button>
                      </span>
                    </div>
                  </div>
                  <div className="btn-row">
                    <button
                      className="btn"
                      onClick={() => toast('Needs a backend to issue presigned upload URLs')}
                    >
                      Connect storage
                    </button>
                  </div>
                  <p className="note" style={{ marginTop: '12px' }}>
                    <b>Needs a backend.</b> Presigned upload URLs have to be issued server-side — an
                    access key that reaches the browser is a key anyone can take. The Files tab and
                    the DocStorage adapter are already wired for it.
                  </p>
                </div>
              </div>
            </section>

            {/* ══ BRANDING & LABELS ══ */}
            <section className="pane" id="pane-branding" hidden={active !== 'branding'}>
              <h3>Branding &amp; labels</h3>
              <p className="sub">
                Rename the product and the vocabulary it uses, so the app speaks your practice's
                language rather than the default.
              </p>
              <div className="sec">
                <div className="sec-head">
                  <h4>Organization</h4>
                </div>
                <div className="grid">
                  <div className="f">
                    <label htmlFor="orgName">Organization / product name</label>
                    <input
                      id="orgName"
                      value={displayName}
                      onChange={(e) => {
                        setDisplayName(e.target.value);
                        markDirty();
                      }}
                    />
                    <span className="help">
                      Shown in the sidebar, page titles and printed headers
                    </span>
                  </div>
                </div>
              </div>
              <div className="sec">
                <div className="sec-head">
                  <h4>Label overrides</h4>
                  <span>Leave blank to keep the default</span>
                </div>
                <div className="tbl-wrap">
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th style={{ width: '190px' }}>Default term</th>
                        <th>Your term</th>
                        <th style={{ width: '60px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {labels.map((l, i) => (
                        <tr key={l.key}>
                          <td>
                            <strong>{l.def}</strong>
                          </td>
                          <td>
                            <input
                              value={l.val}
                              placeholder={l.def}
                              onChange={(e) => {
                                setLabels((xs) =>
                                  xs.map((x, idx) =>
                                    idx === i ? { ...x, val: e.target.value } : x,
                                  ),
                                );
                                markDirty();
                              }}
                              style={{
                                fontFamily: 'inherit',
                                fontSize: '12.5px',
                                padding: '6px 9px',
                                border: '1px solid var(--border)',
                                borderRadius: '7px',
                                width: '100%',
                                maxWidth: '260px',
                                outline: 'none',
                              }}
                            />
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {l.val && (
                              <button
                                className="row-x"
                                aria-label="Clear"
                                onClick={() => {
                                  setLabels((xs) =>
                                    xs.map((x, idx) => (idx === i ? { ...x, val: '' } : x)),
                                  );
                                  markDirty();
                                  toast('Label reset');
                                }}
                              >
                                {ico(I.x, 2.4)}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="btn-row">
                  <button
                    className="btn danger"
                    onClick={() => {
                      setLabels((xs) => xs.map((x) => ({ ...x, val: '' })));
                      markDirty();
                      toast('All labels reset to defaults');
                    }}
                  >
                    Reset all labels to defaults
                  </button>
                </div>
              </div>
            </section>

            {/* ══ NOTIFICATIONS ══ */}
            <section className="pane" id="pane-notifs" hidden={active !== 'notifs'}>
              <h3>Notifications</h3>
              <p className="sub">
                How and when the app interrupts people — in-app, by email, or as a desktop push.
              </p>

              <div className="sec">
                <div className="sec-head">
                  <h4>Channels</h4>
                  <span>{notifSummary}</span>
                </div>
                <div className="tog">
                  <div className="tog-txt">
                    <div className="t">In-app bell</div>
                    <div className="d">Mentions, task assignments, due reminders and alerts</div>
                  </div>
                  <Sw
                    checked={chBell}
                    onChange={(v) => {
                      setChBell(v);
                      markDirty();
                    }}
                  />
                </div>
                <div className="tog">
                  <div className="tog-txt">
                    <div className="t">Email</div>
                    <div className="d">A daily digest to each user's address</div>
                  </div>
                  <Sw
                    checked={chEmail}
                    onChange={(v) => {
                      setChEmail(v);
                      markDirty();
                    }}
                  />
                </div>
                <div className="tog">
                  <div className="tog-txt">
                    <div className="t">Desktop push</div>
                    <div className="d" id="pushPermText">
                      {/* alertshub#8: the real permission of the browser this is open in */}
                      {pushPerm === 'granted' ? (
                        <>
                          Browser permission: <b>allowed</b> in this browser. A notice shows when
                          the EHR isn't the tab in front.
                        </>
                      ) : pushPerm === 'denied' ? (
                        <>
                          Browser permission: <b>blocked</b>. Allow notifications for this site in
                          the browser's settings.
                        </>
                      ) : pushPerm === 'unsupported' ? (
                        <>This browser can't show desktop notifications.</>
                      ) : (
                        <>
                          Browser permission: <b>not requested</b>.{' '}
                          <button
                            type="button"
                            className="btn sm"
                            style={{ marginLeft: 6 }}
                            id="pushAllowBtn"
                            onClick={askPush}
                          >
                            Allow in this browser
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <Sw
                    checked={chPush}
                    onChange={(v) => {
                      setChPush(v);
                      markDirty();
                      if (v && pushPerm === 'default') askPush();
                    }}
                  />
                </div>
                <div className="tog">
                  <div className="tog-txt">
                    <div className="t">Sound</div>
                    <div className="d">
                      A short chime when a new alert or message arrives. Saved for this browser.
                    </div>
                  </div>
                  <Sw
                    checked={sound}
                    onChange={(v) => {
                      setSound(v);
                      setSoundOn(v);
                    }}
                  />
                </div>
              </div>

              <div className="sec">
                <div className="sec-head">
                  <h4>Bucket alerts</h4>
                  <span>When a bucket item sits unclaimed too long</span>
                </div>
                <div className="tog">
                  <div className="tog-txt">
                    <div className="t">Enable bucket alerts</div>
                    <div className="d">
                      Recipients also see ⚠ stale badges in Bucket Preview and Bucket Overview
                    </div>
                  </div>
                  <Sw
                    checked={baOn}
                    onChange={(v) => {
                      setBaOn(v);
                      markDirty();
                    }}
                  />
                </div>
                <div className="grid" style={{ marginTop: '13px' }}>
                  <div className="f">
                    <label htmlFor="baHours">Stale after</label>
                    <Dd
                      value={baHours}
                      onChange={(v) => {
                        setBaHours(v);
                        markDirty();
                      }}
                      searchable={false}
                      ariaLabel="Booking advance notice"
                      options={['4 hours', '24 hours', '48 hours', '3 days'].map((o) => ({
                        value: o,
                        label: o,
                      }))}
                    />
                  </div>
                </div>
                <div className="sec-head" style={{ marginTop: '16px' }}>
                  <h4>Who gets alerted</h4>
                  <span>
                    {alerted.size} of {alertUsers.length}
                  </span>
                </div>
                <div>
                  {alertUsers.map((u) => (
                    <label className="chk" key={u.id}>
                      <input
                        type="checkbox"
                        checked={alerted.has(u.id)}
                        onChange={(e) => {
                          setAlerted((s) => {
                            const n = new Set(s);
                            if (e.target.checked) n.add(u.id);
                            else n.delete(u.id);
                            return n;
                          });
                          markDirty();
                        }}
                      />
                      <span>{u.name}</span>
                      <span className="role">{u.role}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="sec">
                <div className="sec-head">
                  <h4>Lead assignment templates</h4>
                  <span>Attached to Distribution or Redistribution rules</span>
                </div>
                <div className="tbl-wrap">
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th style={{ width: '210px' }}>Template</th>
                        <th>Subject</th>
                        <th style={{ width: '90px' }}>Channel</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {templates.length ? (
                        templates.map((t, i) => (
                          <tr key={t.name + i}>
                            <td>
                              <strong>{t.name}</strong>
                            </td>
                            <td className="muted">{t.subj}</td>
                            <td>
                              <span className={`badge ${t.ch === 'Email' ? 'blue' : 'grey'}`}>
                                {t.ch}
                              </span>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <button
                                className="row-x"
                                aria-label="Remove"
                                onClick={() => {
                                  setTemplates((x) => x.filter((_, idx) => idx !== i));
                                  markDirty();
                                  toast('Template removed');
                                }}
                              >
                                {ico(I.x, 2.4)}
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="empty">
                            No templates yet. Add one to attach to Distribution or Redistribution
                            rules.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="addbar">
                  <div className="f" style={{ flex: '1 1 170px' }}>
                    <label>Template name</label>
                    <input id="ntName" placeholder="e.g. New referral assigned" />
                  </div>
                  <div className="f" style={{ flex: '2 1 230px' }}>
                    <label>Subject</label>
                    <input id="ntSubj" placeholder="You've been assigned {{contact_name}}" />
                  </div>
                  <div className="f" style={{ flex: '0 0 110px' }}>
                    <label>Channel</label>
                    <Dd
                      value={ntCh}
                      onChange={setNtCh}
                      searchable={false}
                      ariaLabel="Template channel"
                      options={['Email', 'In-app'].map((o) => ({ value: o, label: o }))}
                    />
                  </div>
                  <button className="btn primary" onClick={addTemplate}>
                    Add template
                  </button>
                </div>
              </div>
            </section>

            {/* ══ AUTO-ROUTING ══ */}
            <section className="pane" id="pane-clinical" hidden={active !== 'clinical'}>
              <h3>Clinical notes</h3>
              <p className="sub">
                How Credify NotePilot works on progress notes for this practice.
              </p>

              <div className="sec">
                <div className="sec-head">
                  <h4>NotePilot</h4>
                  <span>{dictationOn ? 'Dictation on' : 'Dictation off'}</span>
                </div>
                <div className="tog">
                  <div className="tog-txt">
                    <div className="t">Dictation</div>
                    <div className="d">
                      Lets clinicians dictate into a note with the microphone in each box and in the
                      session summary. The browser's own speech recognition does the transcription,
                      so the audio is sent to the browser maker's speech service (Google for Chrome,
                      Microsoft for Edge, Apple for Safari), outside Credify. Turn this on only if
                      your practice accepts that for session content.
                    </div>
                  </div>
                  <Sw
                    checked={dictationOn}
                    onChange={(v) => {
                      setDictationOn(v);
                      markDirty();
                    }}
                  />
                </div>
                <div className="tog">
                  <div className="tog-txt">
                    <div className="t">Generating</div>
                    <div className="d">
                      NotePilot generates and adjusts note sections with its built-in rule-based
                      generator, inside the browser. Nothing is sent to an AI service.
                    </div>
                  </div>
                </div>
              </div>

              <div className="sec">
                <div className="sec-head">
                  <h4>Service note default codes</h4>
                </div>
                <p className="sub">
                  The billing code a Service note or a Psych testing note starts with. Timed codes
                  (H0038, H2014, 96130, 96136) add units from the session length. No default: the
                  clinician bills it by hand.
                </p>
                <div className="row">
                  {SERVICE_NOTE_KINDS.map((k) => (
                    <div className="f" key={k} style={{ flex: '1 1 200px' }}>
                      <label>{k}</label>
                      <Dd
                        value={svcCodes[k] ?? SERVICE_CODES[k] ?? ''}
                        onChange={(v) => {
                          setSvcCodes((c) => ({ ...c, [k]: v }));
                          markDirty();
                        }}
                        searchable={false}
                        ariaLabel={`${k} default code`}
                        options={[
                          { value: '', label: 'No default' },
                          ...SERVICE_CODE_CHOICES.map((c) => ({
                            value: c,
                            label: c === SERVICE_CODES[k] ? `${c} (built-in)` : c,
                          })),
                        ]}
                      />
                    </div>
                  ))}
                  {/* ehr#726 (PSYCH_TESTING_NOTE_10-30-V4, 10a): Psych testing's default code,
                      96130 or 96136 only; it starts at No default */}
                  <div className="f" style={{ flex: '1 1 200px' }}>
                    <label>{PSYCH_TESTING_KIND}</label>
                    <Dd
                      value={svcCodes[PSYCH_TESTING_KIND] ?? ''}
                      onChange={(v) => {
                        setSvcCodes((c) => ({ ...c, [PSYCH_TESTING_KIND]: v }));
                        markDirty();
                      }}
                      searchable={false}
                      ariaLabel={`${PSYCH_TESTING_KIND} default code`}
                      options={[
                        { value: '', label: 'No default' },
                        ...PSYCH_TESTING_CODE_CHOICES.map((c) => ({
                          value: c,
                          label: cptLabel(c),
                        })),
                      ]}
                    />
                  </div>
                </div>
              </div>

              {/* ehr#571 (PROGRESS_NOTE_10-20-V141 V120): late entry */}
              <div className="sec">
                <div className="sec-head">
                  <h4>Late entry</h4>
                </div>
                <div className="row">
                  <div className="f" style={{ flex: '0 1 420px' }}>
                    <label htmlFor="dsLateHours">
                      Ask for a reason if signed more than this many hours after the session
                    </label>
                    <input
                      id="dsLateHours"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={720}
                      value={lateHours}
                      aria-invalid={!lateHoursOk(lateHours) || undefined}
                      onChange={(e) => {
                        setLateHours(e.target.value);
                        markDirty();
                      }}
                    />
                    {!lateHoursOk(lateHours) && (
                      <span className="ds-field-err" role="alert">
                        Use a whole number of hours from 1 to 720.
                      </span>
                    )}
                  </div>
                </div>
                <p className="sub">
                  A progress note signed or submitted later than this asks the clinician for the
                  reason, which is kept with the note and printed on it. 72 hours by default.
                </p>
              </div>
            </section>

            <section className="pane" id="pane-routing" hidden={active !== 'routing'}>
              <h3>Auto-routing</h3>
              <p className="sub">
                Assign new prospects to a rep automatically. Rules run top to bottom on every new
                prospect — the <em>first</em> match wins. If nothing matches, the fallback runs.
                This applies at creation time; existing prospects aren't reassigned.
              </p>
              <div className="tog" style={{ borderBottom: 'none', paddingTop: 0 }}>
                <div className="tog-txt">
                  <div className="t">Enable auto-routing</div>
                  <div className="d">
                    {routeOn
                      ? 'On — every new prospect runs through the rules below.'
                      : 'Currently off — new prospects start unassigned.'}
                  </div>
                </div>
                <Sw
                  checked={routeOn}
                  onChange={(v) => {
                    setRouteOn(v);
                    markDirty();
                  }}
                />
              </div>
              <div className="sec">
                <div className="sec-head">
                  <h4>Rules, in order</h4>
                  <span>{rules.length + (rules.length === 1 ? ' rule' : ' rules')}</span>
                </div>
                <div>
                  {rules.length ? (
                    rules.map((r, i) => (
                      <div className="rule" key={i}>
                        <span className="ord">{i + 1}</span>
                        <span className="cond" dangerouslySetInnerHTML={html(r.cond)} />
                        <span className="arrow">→</span>
                        <span className="who">{r.who}</span>
                        <button
                          className="row-x"
                          aria-label="Remove rule"
                          style={{ marginLeft: 'auto' }}
                          onClick={() => {
                            setRules((x) => x.filter((_, idx) => idx !== i));
                            markDirty();
                            toast('Rule removed');
                          }}
                        >
                          {ico(I.x, 2.4)}
                        </button>
                      </div>
                    ))
                  ) : (
                    <p
                      style={{
                        fontSize: '12.5px',
                        color: 'var(--text-muted-2)',
                        fontStyle: 'italic',
                        margin: '6px 0 10px',
                      }}
                    >
                      No rules yet — for example, “California agencies → Jennifer”.
                    </p>
                  )}
                </div>
                <div className="btn-row">
                  <button
                    className="btn"
                    onClick={() => {
                      setRules((x) => [
                        ...x,
                        { cond: 'New condition — <b>click to edit</b>', who: 'Unassigned' },
                      ]);
                      markDirty();
                    }}
                  >
                    {ico(I.plus, 2.5)}Add rule
                  </button>
                </div>
              </div>
              <div className="sec">
                <div className="sec-head">
                  <h4>Fallback</h4>
                  <span>When no rule matches</span>
                </div>
                <div className="grid">
                  <div className="f">
                    <label htmlFor="fbKind">Assign to</label>
                    <Dd
                      value={fbKind}
                      onChange={setFbKind}
                      searchable={false}
                      ariaLabel="Fallback assignment"
                      options={[
                        'Leave unassigned',
                        'A specific user',
                        'Round-robin across a group',
                      ].map((o) => ({ value: o, label: o }))}
                    />
                  </div>
                </div>
              </div>
              <p className="note">
                <b>Worth deciding:</b> the settings menu also has <i>Distribution Rules</i> and{' '}
                <i>Redistribution Rules</i>, which cover overlapping ground.
              </p>
            </section>

            {/* ══ BACKUP & DATA ══ */}
            <section className="pane" id="pane-data" hidden={active !== 'data'}>
              <h3>Backup &amp; data</h3>
              <p className="sub">
                Your workspace lives in this browser's storage. Export regularly to back it up or
                move it to another machine.
              </p>

              <div className="sec">
                <div className="sec-head">
                  <h4>Backup &amp; transfer</h4>
                </div>
                <div className="btn-row" style={{ marginTop: 0 }}>
                  <button className="btn primary" onClick={() => setIeModal({ kind: 'contacts' })}>
                    {ico(I.export)}Export…
                  </button>
                  <button className="btn" onClick={() => setIeModal({ kind: 'backup' })}>
                    Download JSON backup
                  </button>
                  <button
                    className="btn"
                    onClick={() => setIeModal({ kind: 'contacts', autoImport: true })}
                  >
                    {ico(I.import)}Import from file
                  </button>
                </div>
                <p className="note" style={{ marginTop: '12px' }}>
                  Last backup: <b>never</b> — the workspace currently holds{' '}
                  <b>{counts?.client ?? '—'}</b> contacts, <b>{counts?.referral_source ?? '—'}</b>{' '}
                  referral sources and <b>{counts?.staff ?? '—'}</b> team members.
                </p>
              </div>

              <div className="sec">
                <div className="sec-head">
                  <h4>Data hygiene</h4>
                  <span>Detection looks at names (typo-tolerant), emails, phones and domains</span>
                </div>
                <div className="btn-row" style={{ marginTop: 0 }}>
                  <button
                    className="btn primary"
                    onClick={() => toast('Opens the duplicate scanner')}
                  >
                    Scan agencies for duplicates
                  </button>
                  <button className="btn" onClick={() => toast('Opens the duplicate scanner')}>
                    Scan therapists for duplicates
                  </button>
                </div>
              </div>

              <div className="danger-zone">
                <h4>Danger zone</h4>
                <p>
                  Resetting wipes every contact, task, document, note and setting in this workspace
                  and cannot be undone. Export first.
                </p>
                <div className="grid" style={{ maxWidth: '420px' }}>
                  <div className="f">
                    <label htmlFor="resetConfirm">
                      Type <b>RESET</b> to enable
                    </label>
                    <input
                      id="resetConfirm"
                      placeholder="RESET"
                      autoComplete="off"
                      value={resetConfirm}
                      onChange={(e) => setResetConfirm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="btn-row">
                  <button
                    className="btn danger"
                    disabled={resetConfirm.trim() !== 'RESET'}
                    onClick={() => toast('Would wipe the workspace — export first')}
                  >
                    Reset everything
                  </button>
                </div>
              </div>
            </section>

            {ieModal && (
              <ImportExportModal
                category="client"
                initialKind={ieModal.kind}
                autoImport={ieModal.autoImport}
                onClose={() => setIeModal(null)}
              />
            )}

            {/* ══ OPT-OUT CENTER (reminders#4) ══ */}
            <section className="pane" id="pane-optouts" hidden={active !== 'optouts'}>
              <h3>Opt-Out Center</h3>
              <p className="sub">
                Every client’s opt-outs in one place: care and marketing messages by text, email and
                call, and AI listening in sessions. Reminders and Outreach read these.
              </p>
              {active === 'optouts' && <OptOutCenter />}
            </section>

            {/* ══ CLIENT VERIFICATION (ehr#309) ══ */}
            <section className="pane" id="pane-verification" hidden={active !== 'verification'}>
              <h3>Client verification</h3>
              <p className="sub">
                How clients prove who they are before a QR code or web link form fills in their
                information.
              </p>
              {active === 'verification' &&
                (verify ? (
                  <ClientVerificationSettings
                    value={verify}
                    canEdit={canEditPractice}
                    onChange={(v) => {
                      setVerify(v);
                      markDirty();
                    }}
                  />
                ) : (
                  <p className="cv-note">
                    {verifyState.error ? 'Could not load the verification settings.' : 'Loading…'}
                  </p>
                ))}
            </section>

            {/* ══ AUTO LOGOUT (ehr#416) ══ */}
            <section className="pane" id="pane-autologout" hidden={active !== 'autologout'}>
              <h3>Auto logout</h3>
              <p className="sub">
                Sign people out after a set time with no activity, for HIPAA. Two minutes before,
                they see a warning and can stay signed in. The two times below save with Save
                changes; a user's own time saves when you apply it.
              </p>
              {aloSeen && <AutoLogoutSettings canEdit={canEditPractice} onPending={onAloPending} />}
            </section>

            {/* ══ AUDIT LOG ══ */}
            <section className="pane" id="pane-audit" hidden={active !== 'audit'}>
              <h3>Audit log</h3>
              <p className="sub">
                Every change across the practice, newest first: who did it, what, and when. Visible
                to users with audit access.
              </p>
              {/* ehr#61: the full Audit Log (user, filters, search, dates, 200 a page, Export
                  CSV), the same as the user menu's Audit log. It loads when the pane opens. */}
              {active === 'audit' && <AuditView embedded />}
            </section>
          </div>
        </div>

        <div className="ds-foot">
          <span className="foot-note">
            {dsFootNote({ practice: dirty, autoLogout: aloDirty, tiers: tiersDirty })}
          </span>
          <span className="spacer" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={onSave}>
            Save changes
          </button>
        </div>
      </div>

      <div className={`ds-toast ${toastShow ? 'show' : ''}`}>{toastMsg}</div>
      {leaveAsk && (
        <ConfirmDialog
          message={
            tiersDirty && aloDirty
              ? 'You have unsaved changes to the sliding scale tiers and Auto logout. Save them before you leave?'
              : aloDirty
                ? 'You have unsaved changes to Auto logout. Save them before you leave?'
                : 'You have unsaved changes to the sliding scale tiers. Save them before you leave?'
          }
          confirmLabel="Save changes"
          altLabel="Leave without saving"
          danger={false}
          focusConfirm
          onClose={() => setLeaveAsk(false)}
          onAlt={() => {
            setLeaveAsk(false);
            onClose();
          }}
          onConfirm={() => {
            setLeaveAsk(false);
            void (async () => {
              // ehr#583: Auto logout first; a bad time or a cancelled question keeps the window open
              const alo = aloDirty ? aloPending.current : null;
              if (alo) {
                setActive('autologout');
                if (!(await alo())) return;
              }
              const save = tiersDirty ? tiersSave.current : null;
              if (!save) return onClose();
              // a tier in red keeps Data & settings open on the Sliding scale pane
              setActive('sliding');
              if (await save()) onClose();
            })();
          }}
        />
      )}
    </div>
  );
}

/* account card (mailboxes / calendars) */
function AcctCard({
  a,
  kind,
  onDisconnect,
  onReconnect,
}: {
  a: Account;
  kind: string;
  onDisconnect: (kind: string, email: string) => void;
  onReconnect?: (kind: string, a: Account) => void;
}) {
  const mark = a.provider.toLowerCase().startsWith('g') ? 'g' : 'o';
  return (
    <div className="prov">
      <div className="prov-head">
        <span className={`prov-mark ${mark}`}>{a.provider[0]}</span>
        <div>
          <div className="nm">{a.email}</div>
          <div className="sb">
            {a.provider}
            {a.note ? ' · ' + a.note : ''}
          </div>
        </div>
        <div className="right">
          {a.status === 'wait' ? (
            <span className="status wait">
              <i />
              Awaiting auth
            </span>
          ) : a.status === 'err' ? (
            // #501 follow-up: the provider refused its sign-in (expired or removed)
            <>
              <span
                className="status err"
                title="Its sign-in expired or was removed: email and calendar updates through it fail"
              >
                <i />
                Reconnect needed
              </span>
              {onReconnect && (
                <button className="btn sm primary" onClick={() => onReconnect(kind, a)}>
                  Reconnect
                </button>
              )}
            </>
          ) : (
            <span className="status on">
              <i />
              Connected
            </span>
          )}
          <button className="btn sm danger" onClick={() => onDisconnect(kind, a.email)}>
            Disconnect
          </button>
        </div>
      </div>
    </div>
  );
}
